// Letris — Word Tetris (vanilla JS) — v5
// Multi-shape + rotación + WASD + 6 niveles fusión + cross-level absorb
// + Mystery box (Mario Kart) + slot machine + power-ups + score scaling

import { POWERUPS, COINS_PER_POINT, LETRIS_POOL, LETRIS_RARITY, LETRIS_MAX_SLOTS, PHASES, BOSSES, MISSION_POOL, letrisCostFor } from './data.js';
import { SFX, startMusic, stopMusic, isMuted as audioIsMuted, setMuted as audioSetMuted, isMusicMuted as audioIsMusicMuted, setMusicMuted as audioSetMusicMuted } from './audio.js';

// ====================== CONSTANTES ======================
const COLS = 10;
const ROWS = 20;
const CELL = 32;
const GAP = 2;
const PAD = 4;
const MAX_LEVEL = 5;

// 6 colores progresivos
const LEVEL_STYLES = [
  { bg: '#00A896', fg: '#FFFFFF' }, // L0 teal
  { bg: '#FFD166', fg: '#121214' }, // L1 amber
  { bg: '#EF476F', fg: '#FFFFFF' }, // L2 coral
  { bg: '#9B5DE5', fg: '#FFFFFF' }, // L3 violet
  { bg: '#F15BB5', fg: '#FFFFFF' }, // L4 pink
  { bg: '#C6F432', fg: '#121214' }, // L5 MAX neon-lime
];
// Crecimiento Suika-style por nivel — niveles "rectangulares" tienen 2 variantes
// (horizontal y vertical), engine elige según dirección de la fusión.
const SIZE_BY_LEVEL = [
  { variants: [{ w: 1, h: 1 }] },                              // L0
  { variants: [{ w: 2, h: 1 }, { w: 1, h: 2 }] },              // L1
  { variants: [{ w: 2, h: 2 }] },                              // L2
  { variants: [{ w: 3, h: 2 }, { w: 2, h: 3 }] },              // L3
  { variants: [{ w: 3, h: 3 }] },                              // L4
  { variants: [{ w: 4, h: 3 }, { w: 3, h: 4 }] },              // L5 MAX
];

// Formas de piezas (cells = [[dr, dc]]). Sin 2x2 (difícil combinar).
const PIECE_SHAPES = [
  { name: '1x1',  cells: [[0,0]],         weight: 6 },
  { name: '1x2',  cells: [[0,0],[0,1]],   weight: 3 },  // horizontal
  { name: '2x1',  cells: [[0,0],[1,0]],   weight: 3 },  // vertical
];
const SHAPE_BAG = (() => {
  const b = [];
  for (const s of PIECE_SHAPES) for (let i = 0; i < s.weight; i++) b.push(s);
  return b;
})();

// Pesos optimizados para formación de palabras: vocales bumped ~50%
// para cubrir mejor el ratio CV típico del español.
const LETTER_BASE_WEIGHTS = {
  A:2000,B:142,C:468,D:586,E:2100,F:69,G:101,H:70,I:1100,
  J:44,L:497,M:315,N:671,O:1500,P:251,Q:88,R:687,S:798,
  T:463,U:700,V:90,X:22,Y:90,Z:52,Ñ:31,
};
const HARD_LETTERS = ['X','Z','K','W','J','Q','Ñ'];
const COMMON_THREE_LETTER_WORDS = new Set([
  'ABA','ABR','AJA','AJI','ALA','ALE','AMA','AME','AMO','ANA','ANO','ARA','ARE','ARO','ASA','ASE','ASI','ATO','AUN','AVE','AYA','AYO','AÑO',
  'BAR','BIS','BOA','BOL','BUS',
  'CAE','CAL','CAN','CAO','CAP','CAR','CAS','CAZ','CES','CIA','CID','CIE','COL','CON','COR','COS','CUA',
  'DAN','DAR','DAS','DEA','DEL','DIA','DIO','DIP','DON','DOS','DUO',
  'ECO','EJE','ELF','ESA','ESE','ESO','ETA',
  'FAN','FAR','FAS','FEA','FEO','FIN','FIO','FUI',
  'GAS','GEL','GOL','GUA','GUI',
  'HAS','HAZ','HAY','HOY',
  'IDA','IDO','IRA','IRE',
  'JAM','JAZ',
  'LAS','LEA','LEE','LEI','LEO','LES','LEY','LIA','LIE','LIO','LOS','LUA','LUI','LUZ',
  'MAL','MAN','MAR','MAS','MES','MIA','MIO','MIL','MIS','MOR','MUY',
  'NAC','NAD','NEO','NOS',
  'OCA','OIR','OJO','OLA','OLE','OSA','OSO',
  'PAN','PAR','PAZ','PEZ','PIE','PIS','POR','POS','PUS',
  'QUE',
  'RAS','RAY','REA','RED','REI','REO','REY','RIO','RON','ROS',
  'SAL','SAN','SED','SER','SIN','SOL','SON','SUR',
  'TAL','TAN','TEA','TEN','TIO','TOS','TRES','TUN',
  'UNA','UNO','UÑA','UÑE','UÑO',
  'VAS','VEO','VER','VES','VEZ','VIA','VIO','VOS','VOY',
  'YIN',
  'ZAR','ZEN',
]);
const LETTER_VALUE = {
  A:1,E:1,I:1,O:1,S:1,N:1,L:1,R:1,U:1,T:1,
  D:2,G:2,B:3,C:3,M:3,P:3,
  H:4,F:4,V:4,Y:4,Q:5,J:8,
  Ñ:8,X:8,Z:10,K:10,W:10,
};
const letterValue = (l) => LETTER_VALUE[l] || 1;

const LEVEL_GOALS = [
  { points:  380,  longWords: 1, longLen: 4, requireFusionWord: false, requireCascadeCombo: false },
  { points: 1100,  longWords: 0, longLen: 0, requireFusionWord: true,  requireCascadeCombo: false },
  { points: 2400,  longWords: 1, longLen: 4, requireFusionWord: false, requireCascadeCombo: false },
  { points: 4300,  longWords: 2, longLen: 5, requireFusionWord: false, requireCascadeCombo: false },
  { points: 7000,  longWords: 0, longLen: 0, requireFusionWord: true,  requireCascadeCombo: true  },
];
function goalForLevel(lvl) {
  const base = LEVEL_GOALS[Math.min(lvl-1, LEVEL_GOALS.length-1)];
  // escala metas con mismo factor que el score → percepción de progreso pero balance constante
  const mult = 1 + (lvl - 1) * 0.42;
  return { ...base, points: Math.round(base.points * mult) };
}

function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ====================== ESTADO ======================
let nextTileId = 1;
const state = makeInitialState();
function makeInitialState() {
  return {
    config: null,
    board: makeEmptyBoard(),
    tiles: new Map(),
    selection: [],
    score: 0,
    bestThisRun: 0,
    history: [],
    piece: null,
    nextPieces: [],
    level: 1,
    levelProgress: { points: 0, longWords: 0, fusionWord: false, cascadeCombo: false },
    gameOver: false,
    paused: false,
    flash: null,
    dropTimer: 0,
    lastTick: performance.now(),
    dictionary: null,
    rng: Math.random,
    timeLeft: null,
    cascadeMergeOccurred: false,
    activeBuffs: [],
    nextWildcard: false,
    levelTime: 0,        // segundos desde el último level up
    dangerLine: 0,       // fila ceiling: tiles en r < dangerLine = game over
    nextDropTime: 40,    // levelTime al que toca próximo descenso de línea
    letris: [],          // Cards activas (max LETRIS_MAX_SLOTS)
    pendingLetris: null, // Card pendiente de equipar/descartar (modal abierto)
    phase: null,
    boss: null,
    mission: null,
    _comboStack: 0,
    _lastWordTs: 0,
    listeners: { onScore:null, onLevelUp:null, onGameOver:null, onWord:null, onCoins:null, onLetrisChange:null },
  };
}
function makeEmptyBoard() { return Array.from({length:ROWS}, () => Array(COLS).fill(null)); }

function dropInterval() {
  let base = state.config?.gravityScales === false ? 700 : 900;
  const fast = state.config?.fastFall ? 0.83 : 1;
  let mod = 1;
  if (hasActiveBuff('slow'))  mod *= 2;
  if (hasActiveBuff('haste')) mod *= 0.5;
  // Presión por intensidad: cada step −7% intervalo (más rápido). Cap 60%.
  const intensity = getIntensity();
  const pressure = Math.min(0.6, intensity * 0.07);
  // Letris gravityMul: <1 = más rápido, >1 = más lento. Aplicado como divisor del intervalo.
  const letrisG = applyLetrisGravityMul();
  return Math.max(70, base * Math.pow(0.82, state.level-1) * fast * mod * (1 - pressure) * letrisG);
}

function levelMultiplier() {
  return 1 + (state.level - 1) * 0.5;
}

function currentPhaseForLevel(level = state.level) {
  let phase = PHASES[0];
  for (const p of PHASES) {
    if (level >= p.fromLevel) phase = p;
  }
  return phase || null;
}

function bossDefForLevel(level = state.level) {
  if (state.config?.duelMode || level < 5 || level % 5 !== 0) return null;
  const idx = Math.min(BOSSES.length - 1, Math.floor(level / 5) - 1);
  return BOSSES[idx];
}

function phaseDangerMul() {
  if (state.config?.duelMode) return 1;
  return state.phase?.dangerMul || 1;
}

function boardFillRatio() {
  let occupied = 0;
  for (const row of state.board) for (const cell of row) if (cell !== null) occupied++;
  return occupied / (ROWS * COLS);
}

// ====================== DANGER LINE (techo descendente con grace por palabras) ======================
const DANGER_BASE_INTERVAL = 40;  // segundos hasta el primer descenso
const DANGER_MIN_INTERVAL  = 15;  // intervalo mínimo (tras varios descensos)
const DANGER_MAX_LINE      = ROWS - 5;  // siempre 5 filas jugables mín

function updateDangerLine() {
  if (state.config?.allowGameOver === false) return;
  // descensos múltiples si grace insuficiente
  while (state.levelTime >= state.nextDropTime && state.dangerLine < DANGER_MAX_LINE) {
    state.dangerLine++;
    state._dangerLineDescended = true;
    // Próximo intervalo se reduce con descensos + intensidad. Letris dangerMul acelera (<1 sería más lento).
    const intensity = getIntensity();
    const dangerMul = applyLetrisDangerMul() * phaseDangerMul();  // >1 = baja más rápido (intervalo / mul)
    let interval = Math.max(DANGER_MIN_INTERVAL,
      DANGER_BASE_INTERVAL - state.dangerLine * 2.5 - intensity * 1.2);
    interval = interval / dangerMul;
    state.nextDropTime += interval;
  }
  // game over si algún tile en zona forbidden (excluye specials para no matar por spawn random)
  if (state.dangerLine > 0) {
    for (const tile of state.tiles.values()) {
      if (tile.special) continue;
      for (const cell of tile.cells) {
        if (cell.r < state.dangerLine) {
          state.gameOver = true;
          state.listeners.onGameOver?.(state.score);
          return;
        }
      }
    }
  }
}

// Cada palabra retrasa el siguiente descenso del techo
function awardDangerGrace(wordLen) {
  if (state.config?.allowGameOver === false) return;
  let grace = 4;
  if (wordLen >= 7)      grace = 22;
  else if (wordLen >= 5) grace = 14;
  else if (wordLen >= 4) grace = 9;
  if (state._directorRelief) grace += state._directorRelief;
  state.nextDropTime += grace;
  state._lastGrace = { amount: grace, ts: performance.now() };
}

// ====================== LETRAS — Bag tipo Tetris + dict-aware ======================
// Frecuencias derivadas DEL diccionario real (83k palabras), ponderadas por
// longitud (1/len²) → palabras cortas pesan más → letras útiles para 3-5 letras.
// Bag balanceado vocal/consonante 50/50, anti-streak (max 3 mismo tipo seguidos),
// raras (Q/X/Z/K/W/Ñ/J) escalan con nivel del juego.

const VOWEL_SET = new Set(['A','E','I','O','U']);
const RARE_CONSONANTS = ['Q', 'J', 'Ñ', 'X', 'Z', 'K', 'W'];

let _dictWeights = null;
let _vowelPool = null;
let _consPool = null;
let _prefixNext = null;

function computeDictWeights() {
  if (_dictWeights || !state.dictionary) return _dictWeights;
  const counts = {};
  for (const word of state.dictionary) {
    const wLen = word.length;
    const wgt = 1 / (wLen * wLen);  // bias palabras cortas
    for (const ch of word) {
      counts[ch] = (counts[ch] || 0) + wgt;
    }
  }
  // normaliza a enteros 1..200 para slots del pool
  const max = Math.max(...Object.values(counts));
  const scaled = {};
  for (const [l, c] of Object.entries(counts)) {
    scaled[l] = Math.max(1, Math.round(c / max * 200));
  }
  _dictWeights = scaled;
  _prefixNext = new Map();
  for (const word of state.dictionary) {
    const maxPrefix = Math.min(5, word.length - 1);
    for (let i = 1; i <= maxPrefix; i++) {
      const prefix = word.slice(0, i);
      const next = word[i];
      if (!next) continue;
      if (!_prefixNext.has(prefix)) _prefixNext.set(prefix, new Map());
      const bucket = _prefixNext.get(prefix);
      bucket.set(next, (bucket.get(next) || 0) + 1);
    }
  }
  // pre-build pools separados
  _vowelPool = [];
  _consPool = [];
  for (const [l, w] of Object.entries(scaled)) {
    if (VOWEL_SET.has(l)) {
      for (let i = 0; i < w; i++) _vowelPool.push(l);
    } else if (!RARE_CONSONANTS.includes(l)) {
      for (let i = 0; i < w; i++) _consPool.push(l);
    }
  }
  return scaled;
}

function refillLetterQueue() {
  // Daily mode: bag derives from unfound target words so player can always form them.
  if (state.config?.targetWords) {
    const bag = buildTargetBag();
    if (bag && bag.length) { state._letterQueue = bag; return; }
  }
  computeDictWeights();
  const vowelPool = _vowelPool || ['A','E','I','O','U'];
  const consPool  = _consPool  || ['R','S','N','L','T','D'];

  const bag = [];
  // 7 vocales + 7 consonantes, peso dict-derived
  for (let i = 0; i < 7; i++) bag.push(vowelPool[Math.floor(state.rng() * vowelPool.length)]);
  for (let i = 0; i < 7; i++) bag.push(consPool[Math.floor(state.rng() * consPool.length)]);
  seedHelpfulLetters(bag);
  // letra rara: chance escala con nivel
  const rareChance = Math.min(0.40, 0.10 + (state.level - 1) * 0.06);
  if (state.rng() < rareChance) {
    const rare = RARE_CONSONANTS[Math.floor(state.rng() * RARE_CONSONANTS.length)];
    bag[bag.length - 1] = rare;
  }
  // Fisher-Yates shuffle
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(state.rng() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  // anti-streak: max 3 del mismo tipo seguidos
  const recent = state._recentLetters || [];
  for (let i = 0; i < bag.length; i++) {
    const last2 = i === 0 ? recent.slice(-2) : bag.slice(Math.max(0, i-2), i);
    if (last2.length < 2) continue;
    const allV = last2.every(l => VOWEL_SET.has(l));
    const allC = last2.every(l => !VOWEL_SET.has(l));
    if (allV && VOWEL_SET.has(bag[i])) {
      for (let j = i + 1; j < bag.length; j++) {
        if (!VOWEL_SET.has(bag[j])) { [bag[i], bag[j]] = [bag[j], bag[i]]; break; }
      }
    } else if (allC && !VOWEL_SET.has(bag[i])) {
      for (let j = i + 1; j < bag.length; j++) {
        if (VOWEL_SET.has(bag[j])) { [bag[i], bag[j]] = [bag[j], bag[i]]; break; }
      }
    }
  }
  state._letterQueue = bag;
}

function seedHelpfulLetters(bag) {
  const helpful = boardHelpfulLetters(4);
  if (!helpful.length) return;
  const inject = Math.min(4, helpful.length, Math.max(1, Math.floor(bag.length * 0.25)));
  for (let i = 0; i < inject; i++) {
    const letter = helpful[Math.floor(state.rng() * helpful.length)];
    const idx = Math.floor(state.rng() * bag.length);
    bag[idx] = letter;
  }
}

function boardHelpfulLetters(limit = 4) {
  if (!_prefixNext || !state.tiles.size) return [];
  const paths = collectBoardPrefixes(limit);
  const weighted = [];
  for (const prefix of paths) {
    const nextMap = _prefixNext.get(prefix);
    if (!nextMap) continue;
    for (const [letter, count] of nextMap) {
      const slots = Math.min(8, Math.max(1, Math.round(Math.log2(count + 1))));
      for (let i = 0; i < slots; i++) weighted.push(letter);
    }
  }
  return weighted;
}

function collectBoardPrefixes(maxDepth) {
  const out = new Set();
  const starts = [...state.tiles.values()].filter(t => !t.special && !t.mystery && !t.blockedUntil);
  for (let i = starts.length - 1; i > 0; i--) {
    const j = Math.floor(state.rng() * (i + 1));
    [starts[i], starts[j]] = [starts[j], starts[i]];
  }
  starts.length = Math.min(starts.length, 28);
  const dirs = neighborDirs();
  function dfs(tile, used, str) {
    if (str.length >= 2) out.add(str);
    if (str.length >= maxDepth) return;
    for (const cell of tile.cells) {
      for (const [dr, dc] of dirs) {
        const r = cell.r + dr, c = cell.c + dc;
        if (r < 0 || r >= ROWS || c < 0 || c >= COLS) continue;
        const id = state.board[r][c];
        if (id == null || used.has(id)) continue;
        const next = state.tiles.get(id);
        if (!next || next.special || next.mystery || next.blockedUntil) continue;
        used.add(id);
        dfs(next, used, str + next.letter);
        used.delete(id);
      }
    }
  }
  for (const t of starts) dfs(t, new Set([t.id]), t.letter);
  return out;
}

function randomLetter() {
  if (!state._letterQueue || state._letterQueue.length === 0) refillLetterQueue();
  const letter = state._letterQueue.shift();
  if (!state._recentLetters) state._recentLetters = [];
  state._recentLetters.push(letter);
  if (state._recentLetters.length > 5) state._recentLetters.shift();
  return letter;
}

// Daily mode bag: union of letter multisets of unfound target words + filler.
// Counts letters already on board so we don't over-stuff with letters already available.
function buildTargetBag() {
  const targets = state.config.targetWords || [];
  const found = state._foundTargets || new Set();
  const unfound = targets.filter(w => !found.has(w));
  if (unfound.length === 0) return null;

  // Letters needed: max count per letter across unfound words (any single word formable).
  const need = {};
  for (const w of unfound) {
    const local = {};
    for (const ch of w) local[ch] = (local[ch] || 0) + 1;
    for (const [ch, n] of Object.entries(local)) {
      if (!need[ch] || n > need[ch]) need[ch] = n;
    }
  }
  // Subtract letters already present on board (loose discount, only floor count).
  const onBoard = {};
  for (const tile of state.tiles.values()) {
    if (tile.special || tile.mystery) continue;
    onBoard[tile.letter] = (onBoard[tile.letter] || 0) + 1;
  }
  const bag = [];
  for (const [ch, n] of Object.entries(need)) {
    const have = onBoard[ch] || 0;
    const missing = Math.max(0, n - have);
    // double the deficit so multiple words sharing a letter still get served
    const inject = missing * 2;
    for (let i = 0; i < inject; i++) bag.push(ch);
  }
  // Filler: ~40% of bag size to keep general-play letters flowing.
  computeDictWeights();
  const vPool = _vowelPool || ['A','E','I','O','U'];
  const cPool = _consPool  || ['R','S','N','L','T','D'];
  const fillerCount = Math.max(8, Math.round(bag.length * 0.6));
  for (let i = 0; i < fillerCount; i++) {
    const pool = state.rng() < 0.45 ? vPool : cPool;
    bag.push(pool[Math.floor(state.rng() * pool.length)]);
  }
  // shuffle
  for (let i = bag.length - 1; i > 0; i--) {
    const j = Math.floor(state.rng() * (i + 1));
    [bag[i], bag[j]] = [bag[j], bag[i]];
  }
  return bag;
}

// ====================== TILES (multi-cell) ======================
function makeTile(letter, level, cells, mystery=false) {
  return { id: nextTileId++, letter, level, mystery, cells: cells.map(c => ({r:c.r, c:c.c})) };
}
function placeTile(tile) {
  state.tiles.set(tile.id, tile);
  for (const {r,c} of tile.cells) state.board[r][c] = tile.id;
}
function removeTile(tile) {
  for (const {r,c} of tile.cells) state.board[r][c] = null;
  state.tiles.delete(tile.id);
  state.selection = state.selection.filter(id => id !== tile.id);
}
function tileAt(r,c) {
  if (r<0||r>=ROWS||c<0||c>=COLS) return null;
  const id = state.board[r][c];
  return id ? state.tiles.get(id) : null;
}
function tileBBox(tile) {
  let minR=Infinity,maxR=-Infinity,minC=Infinity,maxC=-Infinity;
  for (const {r,c} of tile.cells) {
    if (r<minR)minR=r; if (r>maxR)maxR=r;
    if (c<minC)minC=c; if (c>maxC)maxC=c;
  }
  return { minR,maxR,minC,maxC, w:maxC-minC+1, h:maxR-minR+1 };
}

// ====================== PIEZA MULTI-CELL ======================
function makePiece(shape, letters) {
  const cells = shape.cells.map(([dr,dc], i) => ({
    dr, dc, letter: letters[i] || randomLetter(), level: 0,
  }));
  return { shape: shape.name, cells, r: 0, c: 0 };
}

function refillNext() {
  while (state.nextPieces.length < 5) {
    const shape = SHAPE_BAG[Math.floor(state.rng()*SHAPE_BAG.length)];
    const letters = shape.cells.map(() => randomLetter());
    state.nextPieces.push(makePiece(shape, letters));
  }
}

function pieceCells(piece) {
  return piece.cells.map(c => ({ r: piece.r + c.dr, c: piece.c + c.dc, letter: c.letter, level: c.level }));
}

function pieceCanFitAt(piece, r, c) {
  for (const cell of piece.cells) {
    const rr = r + cell.dr, cc = c + cell.dc;
    if (cc < 0 || cc >= COLS || rr >= ROWS) return false;
    if (rr < 0) continue;
    if (state.board[rr][cc] !== null) return false;
  }
  return true;
}

function spawnPiece() {
  refillNext();
  const piece = state.nextPieces.shift();
  refillNext();
  if (state.config?.seedL2 && state.config.seedL2 > 0) {
    piece.cells.forEach(c => c.level = 1);
    state.config.seedL2--;
  }
  if (state.config?.startVowels && state.config.startVowels > 0) {
    piece.cells.forEach(c => { c.letter = state.rng() < 0.75 ? randomVowel() : c.letter; });
    state.config.startVowels--;
  }
  // buff VOCALES activo: convierte todas las celdas a vocales
  if (consumeBuffUse('vowels')) {
    piece.cells.forEach(c => { c.letter = randomVowel(); });
  }
  // Mystery boxes ya no salen dentro de piezas — caen solas random (ver spawnRandomMystery).
  // posiciona centrado
  const maxDc = Math.max(...piece.cells.map(c=>c.dc));
  piece.r = 0;
  piece.c = Math.floor((COLS - (maxDc+1)) / 2);
  if (!pieceCanFitAt(piece, piece.r, piece.c)) {
    if (state.config?.allowGameOver === false) {
      // intentar otra columna
      for (let c=0; c+maxDc<COLS; c++) {
        if (pieceCanFitAt(piece, 0, c)) { piece.c=c; state.piece=piece; return; }
      }
      emergencyClear(2);
      spawnPiece();
      return;
    } else {
      state.gameOver = true;
      state.piece = piece;
      state.listeners.onGameOver?.(state.score);
      return;
    }
  }
  state.piece = piece;
}

function tryMove(dr, dc) {
  if (!state.piece) return false;
  const p = state.piece;
  if (!pieceCanFitAt(p, p.r+dr, p.c+dc)) return false;
  p.r += dr; p.c += dc;
  return true;
}

// Rotación 90° CW: (dr, dc) → (dc, maxDr - dr); luego normaliza a esquina (0,0)
function rotateCells(cells) {
  const maxDr = Math.max(...cells.map(c => c.dr));
  const rotated = cells.map(c => ({
    dr: c.dc, dc: maxDr - c.dr,
    letter: c.letter, level: c.level,
    mystery: c.mystery,  // preserva flag mystery al rotar
  }));
  const minDr = Math.min(...rotated.map(c => c.dr));
  const minDc = Math.min(...rotated.map(c => c.dc));
  rotated.forEach(c => { c.dr -= minDr; c.dc -= minDc; });
  return rotated;
}
function tryRotate() {
  if (!state.piece) return false;
  const p = state.piece;
  const rotatedCells = rotateCells(p.cells);
  const candidate = { ...p, cells: rotatedCells };
  // wall-kick básico: probar offsets
  const kicks = [0, -1, 1, -2, 2];
  for (const k of kicks) {
    if (pieceCanFitAt(candidate, p.r, p.c+k)) {
      p.cells = rotatedCells;
      p.c += k;
      return true;
    }
  }
  return false;
}

function hardDrop() {
  while (tryMove(1,0)) {}
  state._slamLanding = true;
  lockPiece();
  state._slamLanding = false;
  // board shake VFX
  const board = $('board');
  if (board) {
    board.classList.remove('lt-board--shake');
    void board.offsetWidth;
    board.classList.add('lt-board--shake');
    setTimeout(() => board.classList.remove('lt-board--shake'), 220);
  }
}

function lockPiece() {
  const p = state.piece;
  if (!p) return;
  if (p.cells.some(c => p.r + c.dr < 0)) {
    state.gameOver = true; state.piece = null;
    state.listeners.onGameOver?.(state.score);
    return;
  }
  // cada celda → tile 1x1 separado
  const newTiles = [];
  for (const cell of p.cells) {
    const rr = p.r + cell.dr, cc = p.c + cell.dc;
    if (rr < 0) continue;
    const tile = makeTile(cell.letter, cell.level, [{r:rr, c:cc}], cell.mystery);
    placeTile(tile);
    newTiles.push(tile);
    state._justLanded.add(tile.id);
    if (state._slamLanding) {
      state._justSlammed.add(tile.id);
      state._shockwaves.push({ r: rr + 0.5, c: cc + 0.5, w: 1, h: 1 });
    }
  }
  state.piece = null;
  if (state._slamLanding) SFX.tileSlam();
  else SFX.tileLand();

  // SIEMPRE cascade post-lock: celdas flotantes caen + posibles fusiones en chain.
  // NO buscamos auto-words aquí: solo se escanean tras palabra del usuario (combo).
  cascadeAndChain();
  cascadeGravity();

  if (state.config?.allowGameOver !== false) {
    if (state.board[0].some(c => c !== null)) {
      state.gameOver = true;
      state.listeners.onGameOver?.(state.score);
      return;
    }
  }
  spawnPiece();
}

function attemptMergeFromLanding(topTile) {
  if (!state.tiles.has(topTile.id)) return;
  const candidates = new Set();
  for (const {r,c} of topTile.cells) {
    const below = tileAt(r+1, c);
    if (below && below.id !== topTile.id) candidates.add(below);
  }
  for (const bottom of candidates) {
    if (canMerge(topTile, bottom)) {
      if (tryGrow(bottom, topTile)) {
        cascadeAndChain();
        return;
      }
    }
  }
}

function canMerge(bottom, top) {
  // misma letra, no mystery, no blocker, no bloqueada por bomba, bottom < MAX.
  if (bottom.mystery || top.mystery) return false;
  if (bottom.special === 'blocker' || top.special === 'blocker') return false;
  const now = performance.now();
  if ((bottom.blockedUntil && bottom.blockedUntil > now) ||
      (top.blockedUntil && top.blockedUntil > now)) return false;
  if (bottom.letter !== top.letter) return false;
  if (bottom.level >= MAX_LEVEL) return false;
  return true;
}

// Fusión: top siempre muere, bottom crece +1 nivel. Cross-level → bonus pts extra.
function attemptMergeBetween(bottom, top) {
  if (!canMerge(bottom, top)) return false;
  // Bonus de cross-level: si top tenía nivel mayor que bottom, da puntos extra
  // (recompensa "absorber" letras más maduras al crecer).
  if (top.level > bottom.level) {
    const bonus = letterValue(top.letter) * (top.level - bottom.level) * 12;
    state.score += bonus;
    if (state.score > state.bestThisRun) state.bestThisRun = state.score;
    state.levelProgress.points = state.score;
    const topBB = tileBBox(top);
    const bottomBB = tileBBox(bottom);
    state._absorbs.push({
      sr: topBB.minR + topBB.h / 2 - 0.5,
      sc: topBB.minC + topBB.w / 2 - 0.5,
      tr: bottomBB.minR + bottomBB.h / 2 - 0.5,
      tc: bottomBB.minC + bottomBB.w / 2 - 0.5,
      letter: top.letter,
      color: LEVEL_STYLES[top.level].bg,
      fg: LEVEL_STYLES[top.level].fg,
      bonus,
    });
  }
  return tryGrow(bottom, top);
}

function tryGrow(bottomTile, topTile) {
  const newLevel = bottomTile.level + 1;
  const sizeSpec = SIZE_BY_LEVEL[newLevel];
  const variants = sizeSpec.variants.slice();

  // Si hay >1 variante, elegir según dirección dominante de la fusión
  if (variants.length > 1) {
    const bbA = tileBBox(bottomTile), bbB = tileBBox(topTile);
    const dr = Math.abs((bbA.minR + bbA.maxR) / 2 - (bbB.minR + bbB.maxR) / 2);
    const dc = Math.abs((bbA.minC + bbA.maxC) / 2 - (bbB.minC + bbB.maxC) / 2);
    const horizMerge = dc >= dr;  // empate → horizontal (suika feel)
    variants.sort((a, b) => {
      const aHoriz = a.w >= a.h;
      const bHoriz = b.w >= b.h;
      if (aHoriz === bHoriz) return 0;
      return horizMerge ? (Number(bHoriz) - Number(aHoriz)) : (Number(aHoriz) - Number(bHoriz));
    });
  }

  for (const target of variants) {
    if (tryPlaceGrowth(bottomTile, topTile, target, newLevel)) return true;
  }
  return false;
}

function tryPlaceGrowth(bottomTile, topTile, target, newLevel) {
  const bbA = tileBBox(bottomTile), bbB = tileBBox(topTile);
  const unionMinR = Math.min(bbA.minR, bbB.minR);
  const unionMaxR = Math.max(bbA.maxR, bbB.maxR);
  const unionMinC = Math.min(bbA.minC, bbB.minC);
  const unionMaxC = Math.max(bbA.maxC, bbB.maxC);
  const ucr = (unionMinR + unionMaxR) / 2;
  const ucc = (unionMinC + unionMaxC) / 2;

  // Genera todas las posiciones válidas del board, ranquea por:
  //   1) cercanía al centro de la unión (queremos cubrir ambos)
  //   2) bias hacia ABAJO (suika physics, tile reposa donde puede)
  const candidates = [];
  for (let r0 = 0; r0 <= ROWS - target.h; r0++) {
    for (let c0 = 0; c0 <= COLS - target.w; c0++) {
      const ncr = r0 + (target.h - 1) / 2;
      const ncc = c0 + (target.w - 1) / 2;
      const dist = Math.hypot(ncr - ucr, ncc - ucc);
      // bias bottom: penaliza posiciones altas (r0 bajo)
      const bottomBias = (ROWS - r0) * 0.02;
      candidates.push({ r0, c0, score: dist + bottomBias });
    }
  }
  candidates.sort((a, b) => a.score - b.score);

  for (const { r0, c0 } of candidates) {
    const newCells = [];
    for (let dr = 0; dr < target.h; dr++) for (let dc = 0; dc < target.w; dc++) {
      newCells.push({ r: r0 + dr, c: c0 + dc });
    }

    // Obstáculos: tiles ajenos cuyo cell cae dentro del nuevo footprint
    const obstacles = new Map();
    for (const { r, c } of newCells) {
      const occ = state.board[r][c];
      if (occ === null || occ === bottomTile.id || occ === topTile.id) continue;
      const t = state.tiles.get(occ);
      if (t) obstacles.set(t.id, t);
    }

    // Calcula shift up para cada obstáculo
    const shifts = new Map();
    let canPush = true;
    const now = performance.now();
    for (const obs of obstacles.values()) {
      // Bloqueados (post-bomba) y blockers son inamovibles → posición no válida.
      if ((obs.blockedUntil && obs.blockedUntil > now) || obs.special === 'blocker') {
        canPush = false; break;
      }
      let shift = 0;
      for (const cell of obs.cells) {
        if (cell.r >= r0 && cell.r < r0 + target.h && cell.c >= c0 && cell.c < c0 + target.w) {
          const need = cell.r - (r0 - 1);
          if (need > shift) shift = need;
        }
      }
      const obsBB = tileBBox(obs);
      if (obsBB.minR - shift < 0) { canPush = false; break; }
      for (const cell of obs.cells) {
        const newR = cell.r - shift;
        if (newR < 0) { canPush = false; break; }
        const dest = state.board[newR][cell.c];
        if (dest !== null && dest !== obs.id && dest !== bottomTile.id && dest !== topTile.id && !obstacles.has(dest)) {
          canPush = false; break;
        }
      }
      if (!canPush) break;
      shifts.set(obs.id, shift);
    }
    if (!canPush) continue;

    const footprint = new Set(newCells.map(({ r, c }) => `${r},${c}`));
    const occupiedAfterPush = new Map();
    for (const [id, shift] of shifts) {
      const obs = state.tiles.get(id);
      if (!obs) { canPush = false; break; }
      for (const cell of obs.cells) {
        const nr = cell.r - shift;
        const key = `${nr},${cell.c}`;
        if (footprint.has(key)) { canPush = false; break; }
        const prev = occupiedAfterPush.get(key);
        if (prev != null && prev !== id) { canPush = false; break; }
        const dest = state.board[nr]?.[cell.c] ?? null;
        if (dest !== null && dest !== id && dest !== bottomTile.id && dest !== topTile.id && !obstacles.has(dest)) {
          canPush = false; break;
        }
        occupiedAfterPush.set(key, id);
      }
      if (!canPush) break;
    }
    if (!canPush) continue;

    // Push obstáculos (más altos primero)
    const sortedShifts = [...shifts.entries()].sort((a, b) => {
      const ta = state.tiles.get(a[0]), tb = state.tiles.get(b[0]);
      return tileBBox(ta).minR - tileBBox(tb).minR;
    });
    for (const [id, shift] of sortedShifts) {
      if (shift === 0) continue;
      const t = state.tiles.get(id);
      for (const cell of t.cells) state.board[cell.r][cell.c] = null;
      for (const cell of t.cells) cell.r -= shift;
      for (const cell of t.cells) state.board[cell.r][cell.c] = t.id;
      state._justPushed.add(id);
    }

    // Merge + grow
    removeTile(topTile);
    for (const cell of bottomTile.cells) state.board[cell.r][cell.c] = null;
    bottomTile.cells = newCells.map(c => ({ r: c.r, c: c.c }));
    bottomTile.level = newLevel;
    for (const cell of bottomTile.cells) state.board[cell.r][cell.c] = bottomTile.id;
    state._justMerged.add(bottomTile.id);
    state._shockwaves.push({ r: r0 + target.h / 2 - 0.5, c: c0 + target.w / 2 - 0.5, w: target.w, h: target.h });
    SFX.merge(newLevel);
    return true;
  }
  return false;
}

function cascadeAndChain() {
  let safety = 50;
  let firstPass = true;
  let chainCount = 0;
  while (safety-- > 0) {
    cascadeGravity();
    let merged = false;
    const tiles = [...state.tiles.values()];
    // Solo fusión "top hits bottom": para cada bottom candidato, mira fila justo encima.
    outer: for (const bottom of tiles) {
      const bb = tileBBox(bottom);
      const aboveRow = bb.minR - 1;
      if (aboveRow < 0) continue;
      const seen = new Set();
      for (let c = bb.minC; c <= bb.maxC; c++) {
        const top = tileAt(aboveRow, c);
        if (!top || top.id === bottom.id || seen.has(top.id)) continue;
        seen.add(top.id);
        if (attemptMergeBetween(bottom, top)) {
          merged = true;
          chainCount++;
          if (!firstPass) state.cascadeMergeOccurred = true;
          break outer;
        }
      }
    }
    firstPass = false;
    if (!merged) break;
  }
  cascadeGravity();
  if (chainCount >= 2) state._comboBanner = chainCount;
  return chainCount;
}

function cascadeGravity() {
  const now = performance.now();
  let any = true;
  let safety = 200;
  while (any && safety-- > 0) {
    any = false;
    const sorted = [...state.tiles.values()].sort((a,b) => {
      const ba = Math.max(...a.cells.map(c => c.r));
      const bb = Math.max(...b.cells.map(c => c.r));
      return bb - ba;
    });
    for (const tile of sorted) {
      // Tiles bloqueadas (post-bomba) o blocker permanecen ancladas.
      if (tile.blockedUntil && tile.blockedUntil > now) continue;
      if (tile.special === 'blocker') continue;
      const canDrop = tile.cells.every(({r,c}) => {
        if (r+1 >= ROWS) return false;
        const occ = state.board[r+1][c];
        return occ === null || occ === tile.id;
      });
      if (canDrop) {
        for (const cell of tile.cells) state.board[cell.r][cell.c] = null;
        for (const cell of tile.cells) cell.r += 1;
        for (const cell of tile.cells) state.board[cell.r][cell.c] = tile.id;
        any = true;
      }
    }
  }
}

function emergencyClear(rows) {
  const threshold = ROWS - rows;
  const toRemove = [];
  for (const tile of state.tiles.values()) {
    const maxR = Math.max(...tile.cells.map(c => c.r));
    if (maxR >= threshold) toRemove.push(tile);
  }
  toRemove.forEach(t => removeTile(t));
  cascadeGravity();
}

// ====================== SELECCIÓN ======================
function neighborDirs() {
  return [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[-1,1],[1,-1],[1,1]];
}

function tilesAdjacent(a, b) {
  // 8 direcciones. Con diagonal se forman rutas 4-6 mucho antes sin bajar tensión.
  for (const ca of a.cells) for (const cb of b.cells) {
    const dr = Math.abs(ca.r - cb.r), dc = Math.abs(ca.c - cb.c);
    if (dr <= 1 && dc <= 1 && dr + dc > 0) return true;
  }
  return false;
}
function rejectTileVfx(tileId) {
  const el = document.querySelector(`.lt-tile-abs[data-tile-id="${tileId}"]`);
  if (!el) return;
  el.classList.remove('lt-tile--reject');
  void el.offsetWidth;
  el.classList.add('lt-tile--reject');
  setTimeout(() => el.classList.remove('lt-tile--reject'), 380);
  SFX.reject();
}

// Maneja un "hit" de selección (tap o drag-over). isTap = pointerup sin movimiento.
function handleSelectHit(tileId, isTap) {
  if (state.paused || state.gameOver) return;
  const tile = state.tiles.get(tileId);
  if (!tile) return;
  resetHintTimer();
  if (tile.mystery) {
    if (isTap) triggerSlotMachine(tileId);
    return;
  }
  // Tiles bloqueados (post-bomb) o congelados (ice) no se pueden seleccionar
  if (tile.blockedUntil && performance.now() < tile.blockedUntil) {
    rejectTileVfx(tileId);
    return;
  }
  if (tile.special === 'ice' || tile.special === 'blocker') {
    rejectTileVfx(tileId);
    return;
  }
  const sel = state.selection;
  const idx = sel.indexOf(tileId);
  if (idx >= 0) {
    if (isTap && idx === sel.length - 1) {
      // Tap sobre último seleccionado → deselecciona (atrás)
      sel.pop();
    } else {
      // Drag/tap retrocediendo: corta hasta este tile (incluido)
      state.selection = sel.slice(0, idx + 1);
    }
    render();
    return;
  }
  if (sel.length > 0) {
    const last = state.tiles.get(sel[sel.length - 1]);
    if (!last || !tilesAdjacent(last, tile)) {
      rejectTileVfx(tileId);
      return;
    }
  }
  sel.push(tileId);
  render();
}

function flashBoardChromatic(intensity = 1) {
  const board = $('board');
  if (!board) return;
  const flash = document.createElement('div');
  flash.className = 'lt-board-chroma';
  flash.style.setProperty('--chroma-intensity', Math.min(intensity, 6));
  board.appendChild(flash);
  setTimeout(() => flash.remove(), 700);
}

function emitLevelUpBurst() {
  const board = $('board');
  if (!board) return;
  const w = COLS * (CELL + GAP) + GAP + PAD * 2;
  const h = ROWS * (CELL + GAP) + GAP + PAD * 2;
  // Anillo expansivo
  const ring = document.createElement('div');
  ring.className = 'lt-levelup-ring';
  ring.style.left = (w / 2) + 'px';
  ring.style.top = (h / 2) + 'px';
  board.appendChild(ring);
  setTimeout(() => ring.remove(), 1200);
  // Partículas radiales
  for (let i = 0; i < 24; i++) {
    const p = document.createElement('div');
    p.className = 'lt-levelup-spark';
    const angle = (i / 24) * Math.PI * 2;
    const dist = 180 + Math.random() * 60;
    p.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
    p.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
    p.style.left = (w / 2) + 'px';
    p.style.top = (h / 2) + 'px';
    p.style.setProperty('--hue', (i * 15) % 360);
    p.style.animationDelay = (i * 18) + 'ms';
    board.appendChild(p);
    setTimeout(() => p.remove(), 1400 + i * 18);
  }
  shakeBoard('big');
}

function shakeBoard(intensity = 'normal') {
  const board = $('board');
  if (!board) return;
  const cls = intensity === 'big' ? 'lt-board--shake-big' : 'lt-board--shake';
  board.classList.remove('lt-board--shake', 'lt-board--shake-big');
  void board.offsetWidth;
  board.classList.add(cls);
  setTimeout(() => board.classList.remove(cls), intensity === 'big' ? 480 : 220);
}

let _gridPointerNode = null;
let _drag = { active: false, pointerId: null, startId: null, lastHitId: null, moved: false, mysteryStart: false };
let _gest = { active: false, pointerId: null, startX: 0, startY: 0, anchorX: 0, anchorY: 0, moved: false, ended: false };

function setupGridPointer() {
  const grid = $('grid');
  if (!grid) return;
  if (_gridPointerNode === grid) return;
  _gridPointerNode = grid;
  grid.style.touchAction = 'none';

  function tileIdFromPoint(x, y) {
    const elUnder = document.elementFromPoint(x, y);
    if (!elUnder) return null;
    const tileEl = elUnder.closest && elUnder.closest('.lt-tile-abs');
    if (!tileEl) return null;
    const idStr = tileEl.dataset.tileId;
    if (!idStr) return null;  // pieza activa: lt-tile--falling, sin data-tile-id
    const id = parseInt(idStr, 10);
    return Number.isFinite(id) ? id : null;
  }

  grid.addEventListener('pointerdown', (e) => {
    if (state.paused || state.gameOver) return;
    const id = tileIdFromPoint(e.clientX, e.clientY);
    if (id != null) {
      // === Modo selección de palabra ===
      const tile = state.tiles.get(id);
      if (!tile) return;
      e.preventDefault();
      _drag.active = true;
      _drag.pointerId = e.pointerId;
      _drag.startId = id;
      _drag.lastHitId = null;
      _drag.moved = false;
      _drag.mysteryStart = !!tile.mystery;
      try { grid.setPointerCapture(e.pointerId); } catch {}
      if (!tile.mystery) {
        _drag.lastHitId = id;
        handleSelectHit(id, false);
      }
      return;
    }
    // === Modo gesto pieza (toque en pieza activa o espacio vacío) ===
    if (!state.piece) return;
    e.preventDefault();
    _gest.active = true;
    _gest.pointerId = e.pointerId;
    _gest.startX = _gest.anchorX = e.clientX;
    _gest.startY = _gest.anchorY = e.clientY;
    _gest.moved = false;
    _gest.ended = false;
    try { grid.setPointerCapture(e.pointerId); } catch {}
  });

  grid.addEventListener('pointermove', (e) => {
    if (_drag.active && e.pointerId === _drag.pointerId) {
      const id = tileIdFromPoint(e.clientX, e.clientY);
      if (id == null || id === _drag.lastHitId) return;
      _drag.moved = true;
      _drag.lastHitId = id;
      handleSelectHit(id, false);
      return;
    }
    if (_gest.active && e.pointerId === _gest.pointerId && !_gest.ended) {
      const cellSize = Math.max(20, grid.getBoundingClientRect().width / COLS);
      const stepX = cellSize * 0.6;
      const stepY = cellSize * 0.6;
      const totalDX = e.clientX - _gest.startX;
      const totalDY = e.clientY - _gest.startY;
      // Hard drop: swipe vertical hacia abajo dominante y largo
      if (totalDY > cellSize * 1.6 && Math.abs(totalDY) > Math.abs(totalDX) * 1.4) {
        hardDrop();
        render();
        _gest.ended = true;
        _gest.moved = true;
        return;
      }
      const dx = e.clientX - _gest.anchorX;
      const dy = e.clientY - _gest.anchorY;
      // Paso horizontal por cellSize
      if (Math.abs(dx) >= stepX && Math.abs(dx) >= Math.abs(dy)) {
        const dir = dx > 0 ? 1 : -1;
        if (tryMove(0, dir)) render();
        _gest.anchorX = e.clientX;
        _gest.anchorY = e.clientY;
        _gest.moved = true;
        return;
      }
      // Paso vertical hacia abajo (soft drop por celda)
      if (dy >= stepY && dy > Math.abs(dx)) {
        if (tryMove(1, 0)) { state.dropTimer = 0; render(); }
        _gest.anchorX = e.clientX;
        _gest.anchorY = e.clientY;
        _gest.moved = true;
        return;
      }
    }
  });

  function endPointer(e) {
    if (_drag.active && e.pointerId === _drag.pointerId) {
      try { grid.releasePointerCapture(e.pointerId); } catch {}
      if (_drag.moved) {
        if (state.selection.length >= 3) submitWord({ clearOnInvalid: true });
        else { state.selection = []; render(); }
      } else {
        handleSelectHit(_drag.startId, true);
      }
      _drag.active = false;
      _drag.pointerId = null;
      _drag.startId = null;
      _drag.lastHitId = null;
      _drag.moved = false;
      _drag.mysteryStart = false;
      return;
    }
    if (_gest.active && e.pointerId === _gest.pointerId) {
      try { grid.releasePointerCapture(e.pointerId); } catch {}
      // Tap sin movimiento → rotar (sólo con ratón / pen — en touch hay botón).
      if (!_gest.moved && !state.paused && !state.gameOver && e.pointerType !== 'touch') {
        if (tryRotate()) render();
      }
      _gest.active = false;
      _gest.pointerId = null;
      _gest.moved = false;
      _gest.ended = false;
    }
  }
  grid.addEventListener('pointerup', endPointer);
  grid.addEventListener('pointercancel', endPointer);
}

// ====================== POWER-UPS ======================
function hasActiveBuff(id) {
  const now = performance.now();
  return state.activeBuffs.some(b => b.id === id && (b.expiresAt == null || b.expiresAt > now));
}
function consumeBuffUse(id) {
  const b = state.activeBuffs.find(b => b.id === id && b.usesLeft);
  if (!b) return false;
  b.usesLeft--;
  if (b.usesLeft <= 0) state.activeBuffs = state.activeBuffs.filter(x => x !== b);
  return true;
}
function pruneExpiredBuffs() {
  const now = performance.now();
  state.activeBuffs = state.activeBuffs.filter(b => b.expiresAt == null || b.expiresAt > now);
}

function applyPowerup(id) {
  const def = POWERUPS.find(p => p.id === id);
  if (!def) return;
  switch (id) {
    case 'slow':
      state.activeBuffs.push({ ...def, expiresAt: performance.now() + 25000 });
      break;
    case 'score2x':
      state.activeBuffs.push({ ...def, usesLeft: 3 });
      break;
    case 'clear':
      emergencyClear(1);
      break;
    case 'upgrade':
      upgradeRandomTile();
      break;
    case 'coins':
      state.listeners.onCoins?.(80);
      // Coin VFX desde el centro del board (mystery tile ya removed al llegar aquí)
      spawnCoinBurstAtBoardPos(ROWS / 2, COLS / 2, 80);
      break;
    case 'vowels':
      // próximas 3 piezas: todas las celdas son vocales aleatorias
      state.activeBuffs.push({ ...def, usesLeft: 3 });
      break;
    case 'rare-x3':
      state.activeBuffs.push({ ...def, expiresAt: performance.now() + 15000 });
      break;
    case 'haste':
      state.activeBuffs.push({ ...def, expiresAt: performance.now() + 15000 });
      break;
    case 'shuffle':
      shuffleRandomTiles(5);
      break;
  }
}

function upgradeRandomTile() {
  const tiles = [...state.tiles.values()].filter(t => t.level < MAX_LEVEL && !t.mystery);
  if (tiles.length === 0) return;
  const t = tiles[Math.floor(state.rng() * tiles.length)];
  t.level++;
  state._justMerged.add(t.id);
  state._shockwaves.push({
    r: tileBBox(t).minR + tileBBox(t).h/2 - 0.5,
    c: tileBBox(t).minC + tileBBox(t).w/2 - 0.5,
    w: tileBBox(t).w, h: tileBBox(t).h,
  });
}
function shuffleRandomTiles(n) {
  const tiles = [...state.tiles.values()].filter(t => !t.mystery);
  for (let i = 0; i < Math.min(n, tiles.length); i++) {
    const t = tiles[Math.floor(state.rng() * tiles.length)];
    t.letter = randomLetter();
    state._justPushed.add(t.id);
  }
}
const VOWELS = ['A', 'E', 'I', 'O', 'U'];
function randomVowel() { return VOWELS[Math.floor(state.rng() * VOWELS.length)]; }
function pickWeightedLetter() {
  computeDictWeights();
  const isVowel = state.rng() < 0.45;
  const pool = isVowel
    ? (_vowelPool && _vowelPool.length ? _vowelPool : ['A','E','I','O','U'])
    : (_consPool  && _consPool.length  ? _consPool  : ['R','S','N','L','T','D']);
  return pool[Math.floor(state.rng() * pool.length)];
}

// ====================== SLOT MACHINE ======================
function triggerSlotMachine(tileId) {
  if (document.querySelector('.lt-slot-overlay')) return; // ya hay uno
  state.paused = true;
  render();

  // elige ganador (75% buff / 25% debuff)
  const buffs = POWERUPS.filter(p => p.kind === 'buff');
  const debuffs = POWERUPS.filter(p => p.kind === 'debuff');
  const pool = state.rng() < 0.75 ? buffs : debuffs;
  const winner = pool[Math.floor(state.rng() * pool.length)];

  // construye reel
  const overlay = document.createElement('div');
  overlay.className = 'lt-slot-overlay';
  const card = document.createElement('div');
  card.className = 'lt-slot-card';
  card.innerHTML = `<div class="lt-slot-title">¡CAJA SORPRESA!</div>`;

  const reelWrap = document.createElement('div');
  reelWrap.className = 'lt-slot-reelwrap';
  const reel = document.createElement('div');
  reel.className = 'lt-slot-reel';

  // genera ~25 items, ganador en posición 20 (centrado al final del scroll)
  const items = [];
  for (let i = 0; i < 25; i++) {
    items.push(POWERUPS[Math.floor(Math.random() * POWERUPS.length)]);
  }
  items[20] = winner;
  for (const item of items) {
    const slot = document.createElement('div');
    slot.className = 'lt-slot-item';
    slot.style.background = item.color;
    slot.style.color = item.kind === 'debuff' ? '#fff' : '#000';
    slot.innerHTML = `<div class="lt-slot-icon">${item.icon}</div><div class="lt-slot-name">${item.name}</div>`;
    reel.appendChild(slot);
  }
  reelWrap.appendChild(reel);
  card.appendChild(reelWrap);

  const result = document.createElement('div');
  result.className = 'lt-slot-result';
  result.style.opacity = '0';
  card.appendChild(result);

  const btn = document.createElement('button');
  btn.className = 'lt-btn lt-btn--primary';
  btn.textContent = 'GIRANDO...';
  btn.disabled = true;
  card.appendChild(btn);

  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // anima scroll
  const itemH = 84;
  const targetIdx = 20;
  const totalScroll = targetIdx * itemH;
  reel.style.transform = 'translateY(0)';
  requestAnimationFrame(() => {
    reel.style.transition = 'transform 2.6s cubic-bezier(0.18, 0.65, 0.18, 1)';
    reel.style.transform = `translateY(-${totalScroll}px)`;
  });

  setTimeout(() => {
    result.innerHTML = `<strong>${winner.name}</strong><br><span class="lt-slot-desc">${winner.desc}</span>`;
    result.style.opacity = '1';
    result.style.color = winner.color;
    btn.textContent = '¡APLICADO!';
    btn.classList.add('lt-btn--ghost');
    btn.classList.remove('lt-btn--primary');
    // auto-apply 1.1s tras parar (sin click)
    setTimeout(() => {
      applyPowerup(winner.id);
      const t = state.tiles.get(tileId);
      if (t) removeTile(t);
      cascadeAndChain();
      overlay.remove();
      state.paused = false;
      render();
    }, 1100);
  }, 2700);
}

function currentWord() {
  return state.selection.map(id => state.tiles.get(id)?.letter || '').join('');
}
// Length bonus curva: penaliza 3-letras (×0.7) — incentiva formar palabras más largas.
const LENGTH_BONUS = { 3: 0.75, 4: 1.8, 5: 2.8, 6: 4.1, 7: 5.8, 8: 7.6, 9: 9.6, 10: 12.0 };
function lengthBonus(n) {
  if (n <= 3) return 0.75;
  if (n >= 10) return 12.0 + (n - 10) * 1.7;
  return LENGTH_BONUS[n];
}
function pointsForSelection(applyConsumables = false) {
  const tiles = state.selection.map(id => state.tiles.get(id)).filter(Boolean);
  if (!tiles.length) return 0;
  let rareMul = state.config?.rareMultiplier || 1;
  if (hasActiveBuff('rare-x3')) rareMul *= 3;
  const sum = tiles.reduce((acc, t) => {
    const v = letterValue(t.letter) * (HARD_LETTERS.includes(t.letter) ? rareMul : 1);
    return acc + v * (t.level + 1);
  }, 0);
  let pts = sum * tiles.length * lengthBonus(tiles.length);
  if (state.config?.longBonus && tiles.length >= 6) pts = Math.round(pts * state.config.longBonus);
  pts = Math.round(pts * levelMultiplier());
  if (applyConsumables && consumeBuffUse('score2x')) pts *= 2;
  else if (!applyConsumables && hasActiveBuff('score2x')) pts *= 2;
  return pts;
}
function isValidWord(w) {
  const norm = w.toUpperCase();
  return isValidWordString(norm);
}
function isValidWordString(norm) {
  if (norm.length < 3) return false;
  if (!state.dictionary) return false;
  const dictWord = state.dictionary.has(norm) ? norm : (state.dictionary.has(norm.replace(/Ñ/g,'N')) ? norm.replace(/Ñ/g,'N') : null);
  if (!dictWord) return false;
  if (norm.length === 3 && !COMMON_THREE_LETTER_WORDS.has(norm) && !COMMON_THREE_LETTER_WORDS.has(dictWord)) return false;
  return true;
}

function submitWord(opts = {}) {
  if (state.paused || state.gameOver) return;
  if (state.selection.length < 3) return;
  const word = currentWord();
  // mystery tile no se puede usar en palabra (debe clickearse para slot)
  const tilesSel = state.selection.map(id => state.tiles.get(id));
  if (tilesSel.some(t => t?.mystery)) {
    state.flash = { kind: 'invalid', word: 'CLICK ? PARA CAJA' };
    if (opts.clearOnInvalid) state.selection = [];
    SFX.wordInvalid();
    render();
    setTimeout(() => { state.flash = null; render(); }, 900);
    return;
  }
  if (!isValidWord(word)) {
    state.flash = { kind:'invalid', word };
    if (opts.clearOnInvalid) state.selection = [];
    SFX.wordInvalid();
    render();
    setTimeout(() => { state.flash = null; render(); }, 900);
    return;
  }
  SFX.wordValid(word.length);
  const tiles = state.selection.map(id => state.tiles.get(id)).filter(Boolean);
  let basePts = pointsForSelection(true);  // consume buffs
  // Special: bonus ×3 si la palabra incluye tile bonus
  const bonusTiles = tiles.filter(t => t.special === 'bonus');
  if (bonusTiles.length) basePts = Math.round(basePts * Math.pow(3, bonusTiles.length));
  // Aplica hooks Letris al score
  state._letrisTriggered = null;
  const ctx = letrisCtx({ word, tiles, basePts, isAutoWord: false });
  const pts = applyLetrisScore(basePts, ctx);
  const triggeredCards = state._letrisTriggered;
  const usedFusion = tiles.some(t => t.level >= 1);
  // Combo stack reset si pasaron >4s desde última palabra
  const now = performance.now();
  if (now - state._lastWordTs > 4000) state._comboStack = 0;
  state._lastWordTs = now;
  state._comboStack++;
  const sel = state.selection.slice();

  // marcar clearing visual
  for (const id of sel) {
    const el = tileEls.get(id);
    if (el) el.classList.add('lt-tile--clearing');
  }
  state.flash = { kind:'valid', word, points: pts };
  render();

  setTimeout(() => {
    // Captura celdas de la palabra ANTES de eliminar tiles (para auto-words vicinity)
    const originCells = [];
    sel.forEach(id => {
      const t = state.tiles.get(id);
      if (t) for (const cell of t.cells) originCells.push({ r: cell.r, c: cell.c });
    });
    // emite partículas + score popup en cada celda eliminada
    let firstCell = null;
    sel.forEach(id => {
      const t = state.tiles.get(id);
      if (t) {
        for (const cell of t.cells) {
          emitParticles(cell.r, cell.c, LEVEL_STYLES[t.level].bg);
          if (!firstCell) firstCell = cell;
        }
        removeTile(t);
      }
    });
    if (firstCell) {
      emitScorePop(firstCell.r, firstCell.c, pts);
      emitWordImpactVfx(firstCell.r, firstCell.c, word, pts);
    }
    state.selection = [];
    state.score += pts;
    if (state.score > state.bestThisRun) state.bestThisRun = state.score;
    state.history.unshift({ word, points: pts, level: Math.max(0, ...tiles.map(t => t.level || 0)) });
    state.history = state.history.slice(0, 12);
    // Letris: coin extra y grace extra
    const letrisCoins = applyLetrisCoinFlat(ctx);
    const letrisGrace = applyLetrisGraceFlat(ctx);
    if (letrisCoins > 0) state.listeners.onCoins?.(letrisCoins);
    const triggeredLegendary = !!(triggeredCards && triggeredCards.some(c => c.rarity === 'legendary'));
    const wordEvent = { word, points: pts, level: state.level, usedFusion, triggeredLegendary, cascadeCount: 0 };
    state.listeners.onWord?.(wordEvent);
    // Daily mode: mark target hit and check completion
    if (state.config?.targetWords) {
      if (!state._foundTargets) state._foundTargets = new Set();
      if (state.config.targetWords.includes(word) && !state._foundTargets.has(word)) {
        state._foundTargets.add(word);
        state.listeners.onTargetFound?.({ word, found: state._foundTargets.size, total: state.config.targetWords.length });
        // refill bag immediately so deficit recomputes for remaining targets
        state._letterQueue = [];
        if (state._foundTargets.size >= state.config.targetWords.length) {
          state.listeners.onAllTargets?.();
          state.gameOver = true;
          state.listeners.onGameOver?.(state.score);
        }
      }
    }
    awardDangerGrace(word.length + (letrisGrace > 0 ? letrisGrace * 0.4 : 0));
    // Screen shake escalado por longitud de palabra
    if (word.length >= 7) shakeBoard('big');
    else if (word.length >= 5) shakeBoard('normal');
    updateWordStreakVfx(word.length);
    // Coin VFX (incluye letris coins extra)
    const coinAmt = Math.floor(pts * COINS_PER_POINT) + letrisCoins;
    if (firstCell && coinAmt > 0) spawnCoinBurstAtBoardPos(firstCell.r, firstCell.c, coinAmt);
    if (word.length >= 6) state._intensityRelief = (state._intensityRelief || 0) + 1;
    // VFX Letris cards triggered
    if (triggeredCards && firstCell) {
      pulseLetrisCards(triggeredCards);
      flashLetrisOnBoard(triggeredCards, firstCell.r, firstCell.c);
    }
    // Drop Letris card?
    const dropped = rollLetrisDrop(word.length, false);
    if (dropped) openLetrisDropModal(dropped);

    state.levelProgress.points = state.score;
    if (word.length >= 4) state.levelProgress.longWords++;
    if (usedFusion) state.levelProgress.fusionWord = true;

    state.cascadeMergeOccurred = false;
    const beforePos = new Map();
    for (const t of state.tiles.values()) {
      beforePos.set(t.id, t.cells.map(c => c.r + ',' + c.c).join('|'));
    }
    cascadeAndChain();
    // Auto-words SOLO en vecindad de la palabra eliminada (no escanea board entero).
    const cascadeCount = processAutoWords(originCells);
    wordEvent.cascadeCount = cascadeCount;
    for (const t of state.tiles.values()) {
      const after = t.cells.map(c => c.r + ',' + c.c).join('|');
      if (beforePos.get(t.id) !== after && !state._justMerged.has(t.id)) {
        state._justLanded.add(t.id);
      }
    }
    if (state.cascadeMergeOccurred) state.levelProgress.cascadeCombo = true;
    if (state.cascadeMergeOccurred && wordEvent.cascadeCount === 0) wordEvent.cascadeCount = 1;
    updateMissionAfterWord(wordEvent);
    damageBoss(wordEvent);

    state.listeners.onScore?.(state.score);
    checkLevelUp();
    render();
  }, 480);

  setTimeout(() => { state.flash = null; render(); }, 1300);
}

function backspace() { state.selection.pop(); render(); }
function clearSelection() { state.selection = []; render(); }

// ====================== FASES, BOSSES Y MISIONES ======================
function setupPhaseAndMission({ announce = false } = {}) {
  if (state.config?.duelMode) return;
  const nextPhase = currentPhaseForLevel();
  const changed = !state.phase || state.phase.id !== nextPhase?.id;
  state.phase = nextPhase;
  if (!state.mission || changed) state.mission = pickMission();
  const bossDef = bossDefForLevel();
  if (bossDef && (!state.boss || state.boss.level !== state.level || state.boss.id !== bossDef.id)) {
    startBoss(bossDef);
  } else if (!bossDef) {
    state.boss = null;
  }
  if (announce && changed && state.phase) {
    state.flash = { kind: 'levelup', word: state.phase.name, subtitle: state.phase.rule };
    setTimeout(() => { state.flash = null; render(); }, 1800);
  }
}

function pickMission() {
  if (!MISSION_POOL.length) return null;
  const eligible = MISSION_POOL.filter(m => {
    if (m.kind === 'cascades') return state.level >= 3;
    if (m.kind === 'fusionWords') return state.level >= 2;
    if (m.kind === 'rareLetters') return state.level >= 4;
    return true;
  });
  const pool = eligible.length ? eligible : MISSION_POOL;
  const def = pool[Math.floor(state.rng() * pool.length)];
  return { ...def, progress: 0, done: false };
}

function missionProgressLabel(m = state.mission) {
  if (!m) return '';
  return `${Math.min(m.progress, m.target)}/${m.target}`;
}

function applyMissionReward(mission) {
  const r = mission.reward || {};
  if (r.coins) {
    const coins = Math.round(r.coins * (state.config?.missionCoinMul || 1));
    state.listeners.onCoins?.(coins);
    spawnCoinBurstAtBoardPos(ROWS / 2, COLS / 2, coins);
  }
  if (r.grace) {
    state.nextDropTime += r.grace;
    state._lastGrace = { amount: r.grace, ts: performance.now() };
  }
  if (r.clearRows) emergencyClear(r.clearRows);
  state.flash = {
    kind: 'levelup',
    word: 'MISIÓN',
    subtitle: `${mission.label} · +${Math.round((r.coins || 0) * (state.config?.missionCoinMul || 1))} ◈${r.grace ? ` · +${r.grace}s` : ''}`,
  };
  setTimeout(() => { state.flash = null; render(); }, 1500);
}

function updateMissionAfterWord(data) {
  const m = state.mission;
  if (!m || m.done) return;
  let inc = 0;
  if (m.kind === 'longWords' && data.word.length >= m.len) inc = 1;
  else if (m.kind === 'fusionWords' && data.usedFusion) inc = 1;
  else if (m.kind === 'rareLetters') inc = [...data.word].filter(ch => HARD_LETTERS.includes(ch)).length;
  else if (m.kind === 'totalLetters') inc = data.word.length;
  else if (m.kind === 'cascades' && data.cascadeCount > 0) inc = 1;
  if (!inc) return;
  m.progress += inc;
  if (m.progress >= m.target) {
    m.done = true;
    applyMissionReward(m);
    state.mission = pickMission();
  }
}

function startBoss(def) {
  const scale = 1 + Math.max(0, state.level - 5) * 0.18;
  state.boss = {
    ...def,
    level: state.level,
    maxHp: Math.round(def.hp * scale),
    hp: Math.round(def.hp * scale),
    patternTimer: 0,
    patternEvery: Math.max(8, 16 - Math.floor(state.level / 5)) + (state.config?.bossPatternDelay || 0),
  };
  state.flash = { kind: 'levelup', word: def.name, subtitle: def.desc };
  SFX.levelUp();
  shakeBoard('big');
  setTimeout(() => { state.flash = null; render(); }, 2200);
}

function damageBoss(data) {
  if (!state.boss || state.boss.hp <= 0) return 0;
  let dmg = data.word.length;
  if (data.word.length >= 5) dmg += 3;
  if (data.word.length >= 7) dmg += 5;
  if (data.usedFusion) dmg += 4;
  if (data.cascadeCount > 0) dmg += 3 + data.cascadeCount * 2;
  if ([...data.word].some(ch => HARD_LETTERS.includes(ch))) dmg += 3;
  if (state.phase?.longDamageMul && data.word.length >= 5) dmg *= state.phase.longDamageMul;
  if (state.phase?.rareDamageMul && [...data.word].some(ch => HARD_LETTERS.includes(ch))) dmg *= state.phase.rareDamageMul;
  if (state.phase?.cascadeDamageMul && data.cascadeCount > 0) dmg *= state.phase.cascadeDamageMul;
  if (state.config?.bossDamageMul) dmg *= state.config.bossDamageMul;
  dmg = Math.max(1, Math.round(dmg));
  state.boss.hp = Math.max(0, state.boss.hp - dmg);
  state._bossHit = { amount: dmg, ts: performance.now() };
  emitBossDamagePop(dmg);
  if (state.boss.hp <= 0) defeatBoss();
  return dmg;
}

function emitBossDamagePop(amount) {
  const panel = $('boss-panel');
  if (!panel) return;
  const pop = document.createElement('div');
  pop.className = 'lt-boss-damage-pop';
  pop.textContent = '-' + amount;
  panel.appendChild(pop);
  setTimeout(() => pop.remove(), 760);
}

function defeatBoss() {
  const bonus = 120 + state.level * 25;
  const defeated = state.boss;
  state.listeners.onCoins?.(bonus);
  state.nextDropTime += 18;
  emergencyClear(2);
  spawnCoinBurstAtBoardPos(ROWS / 2, COLS / 2, bonus);
  state.flash = { kind: 'levelup', word: 'BOSS DERROTADO', subtitle: `+${bonus} ◈ · tablero despejado` };
  SFX.levelUp();
  shakeBoard('big');
  emitBossDefeatVfx(defeated);
  setTimeout(() => { state.flash = null; render(); }, 2200);
}

function emitBossDefeatVfx(boss) {
  const grid = $('grid');
  if (!grid || !boss) return;
  const blast = document.createElement('div');
  blast.className = 'lt-boss-defeat';
  blast.style.color = boss.color;
  blast.textContent = boss.icon;
  grid.appendChild(blast);
  for (let i = 0; i < 18; i++) {
    const shard = document.createElement('div');
    shard.className = 'lt-boss-shard';
    shard.style.background = boss.color;
    const angle = (i / 18) * Math.PI * 2;
    const dist = 120 + state.rng() * 140;
    shard.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
    shard.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
    shard.style.animationDelay = (i * 12) + 'ms';
    grid.appendChild(shard);
    setTimeout(() => shard.remove(), 1000);
  }
  setTimeout(() => blast.remove(), 1000);
}

function updateBoss(dtSec) {
  if (!state.boss || state.boss.hp <= 0 || state.paused || state.gameOver) return;
  state.boss.patternTimer += dtSec;
  if (state.boss.patternTimer < state.boss.patternEvery) return;
  state.boss.patternTimer = 0;
  const pattern = state.boss.pattern;
  if (pattern === 'blockers') spawnBossBlockers(2 + Math.floor(state.level / 10));
  else if (pattern === 'ice') spawnRandomSpecial('ice');
  else if (pattern === 'bombs') spawnRandomSpecial('bomb');
  else {
    const roll = state.rng();
    if (roll < 0.34) spawnBossBlockers(2);
    else if (roll < 0.67) spawnRandomSpecial('ice');
    else spawnRandomSpecial('bomb');
  }
  state._bossAttack = { ts: performance.now() };
  shakeBoard('normal');
}

function spawnBossBlockers(n) {
  const now = performance.now();
  let placed = 0;
  for (let attempt = 0; attempt < n * 18 && placed < n; attempt++) {
    const r = 3 + Math.floor(state.rng() * 12);
    const c = Math.floor(state.rng() * COLS);
    if (state.board[r][c]) continue;
    const id = nextTileId++;
    const tile = {
      id, letter: '▓', level: 0,
      special: 'blocker',
      blockerUntil: now + 11000,
      cells: [{ r, c }],
    };
    state.tiles.set(id, tile);
    state.board[r][c] = id;
    state._justSlammed.add(id);
    placed++;
  }
}

function bossAliveForCurrentLevel() {
  return !!(bossDefForLevel() && state.boss && state.boss.hp > 0);
}

// ====================== NIVELES ======================
function checkLevelUp() {
  const goal = goalForLevel(state.level);
  const lp = state.levelProgress;
  const pointsOk = lp.points >= goal.points;
  const longOk = goal.longWords === 0 || lp.longWords >= goal.longWords;
  const fusOk = !goal.requireFusionWord || lp.fusionWord;
  const casOk = !goal.requireCascadeCombo || lp.cascadeCombo;
  const bossOk = !bossAliveForCurrentLevel();
  if (pointsOk && longOk && fusOk && casOk && bossOk) levelUp();
}
function levelUp() {
  state.level++;
  state.levelTime = 0;
  state.dangerLine = 0;
  state.nextDropTime = DANGER_BASE_INTERVAL;  // reset reloj de descenso
  emergencyClear(2);
  state.levelProgress = { points: state.score, longWords: 0, fusionWord: false, cascadeCombo: false };
  setupPhaseAndMission({ announce: true });
  // CONFETTI: lluvia de partículas multicolor
  const palette = ['#06D6A0','#FFD166','#EF476F','#9B5DE5','#F15BB5','#C6F432','#00A896'];
  for (let i = 0; i < 60; i++) {
    const c = Math.floor(Math.random() * COLS);
    const r = Math.floor(Math.random() * (ROWS / 2));
    emitParticles(r, c, palette[i % palette.length]);
  }
  // Free buff: bonus lento 8s para respirar
  state.activeBuffs.push({
    id: 'slow', kind: 'buff', name: 'BONUS LENTO', icon: '⏳', color: '#06D6A0',
    expiresAt: performance.now() + 8000,
  });
  emitLevelUpBurst();
  SFX.levelUp();
  // Coins recompensa por nivel
  const coinBonus = 50 + state.level * 30;
  state.listeners.onCoins?.(coinBonus);
  spawnCoinBurstAtBoardPos(ROWS / 2, COLS / 2, coinBonus);
  // Reset alivio intensidad al subir nivel
  state._intensityRelief = 0;
  const bossIntro = state.boss && state.boss.hp > 0 && state.boss.level === state.level;
  state.flash = bossIntro
    ? { kind: 'levelup', word: state.boss.name, subtitle: state.boss.desc }
    : {
        kind: 'levelup',
        word: `NIVEL ${state.level}`,
        subtitle: `+${coinBonus} ◈ · respiro ⏳ · meta x${(1 + (state.level-1)*0.5).toFixed(1)}`,
      };
  state.listeners.onLevelUp?.(state.level);
  setTimeout(() => openLetrisChoiceModal(), 650);
  setTimeout(() => { state.flash = null; render(); }, 2200);
}

// ====================== SPECIAL TILES SPAWN (random, no en piezas) ======================
// Tipos de tiles especiales que caen solos por el tablero.
const SPECIAL_TYPES = {
  mystery: { weight: 50, name: 'CAJA',     icon: '?', desc: 'Click → slot machine de power-up' },
  bonus:   { weight: 24, name: 'BONUS',    icon: '✦', desc: 'Palabra que la incluya: pts ×3' },
  ice:     { weight: 16, name: 'HIELO',    icon: '❄', desc: 'Congelada 18s — no se selecciona ni fusiona' },
  bomb:    { weight: 10, name: 'BOMBA',    icon: '✸', desc: 'Úsala en palabra antes de 18s o bloquea vecinos 30s' },
  blocker: { weight: 0,  name: 'BLOQUE',   icon: '▓', desc: 'Bloque enviado por rival. Decae en 12s.' },
  // wild: TODO (validador de comodín en dict)
};
function pickSpecialType() {
  const phaseWeights = state.config?.duelMode ? null : state.phase?.specialWeights;
  const fill = boardFillRatio();
  const total = Object.entries(SPECIAL_TYPES).reduce((s, [id, t]) => {
    let w = phaseWeights?.[id] ?? t.weight;
    if (fill > 0.55 && (id === 'ice' || id === 'bomb')) w *= 0.55;
    if (fill > 0.62 && (id === 'mystery' || id === 'bonus')) w *= 1.35;
    return s + w;
  }, 0);
  let roll = state.rng() * total;
  for (const [id, t] of Object.entries(SPECIAL_TYPES)) {
    let w = phaseWeights?.[id] ?? t.weight;
    if (fill > 0.55 && (id === 'ice' || id === 'bomb')) w *= 0.55;
    if (fill > 0.62 && (id === 'mystery' || id === 'bonus')) w *= 1.35;
    if (roll < w) return id;
    roll -= w;
  }
  return 'mystery';
}

const SPECIAL_INTERVAL_BASE = 50;
const SPECIAL_INTERVAL_MIN  = 24;
function nextSpecialInterval() {
  const intensity = getIntensity();
  const phaseMul = state.config?.duelMode ? 1 : (state.phase?.intervalMul || 1);
  const panicMul = boardFillRatio() > 0.62 ? 1.35 : 1;
  const interval = Math.max(SPECIAL_INTERVAL_MIN, SPECIAL_INTERVAL_BASE - intensity * 2.2) * phaseMul * panicMul;
  return interval * (0.8 + state.rng() * 0.4);
}
function updateMysterySpawn(dtSec) {
  if (state._mysteryNext == null) state._mysteryNext = nextSpecialInterval();
  state._mysteryTimer = (state._mysteryTimer || 0) + dtSec;
  if (state._mysteryTimer >= state._mysteryNext) {
    state._mysteryTimer = 0;
    state._mysteryNext = nextSpecialInterval();
    spawnRandomSpecial();
  }
  // Update tile timers (bomb fuse, ice thaw, block expire)
  updateSpecialTileTimers();
}
function spawnRandomSpecial(forcedType = null) {
  const existing = [...state.tiles.values()].filter(t => t.special).length;
  const cap = forcedType ? 5 : (state.phase?.id === 'singularity' ? 3 : 2);
  if (existing >= cap) return;  // cap specials simultáneos
  const candidates = [];
  for (let c = 0; c < COLS; c++) {
    if (state.board[0][c] === null) candidates.push(c);
  }
  if (!candidates.length) return;
  const col = candidates[Math.floor(state.rng() * candidates.length)];
  const type = forcedType || pickSpecialType();
  const def = SPECIAL_TYPES[type];
  const id = nextTileId++;
  const now = performance.now();
  // Mystery muestra '?' (no es letra). Resto: letra real + capa especial.
  const letter = type === 'mystery' ? '?' : pickWeightedLetter();
  const tile = {
    id, letter, level: 0, mystery: type === 'mystery',
    special: type, cells: [{ r: 0, c: col }],
  };
  if (type === 'bomb') tile.bombFuseEndAt = now + 18000;
  if (type === 'ice')  tile.iceUntil = now + 18000;
  state.tiles.set(id, tile);
  state.board[0][col] = id;
  // NO cascadeGravity inmediato: queremos verlo caer paso a paso (stepSpecialsGravity en tick)
  tile._slowFalling = true;
  // SFX por tipo
  if (type === 'mystery') SFX.mysterySpawn();
  else if (type === 'ice') SFX.iceFreeze();
  else if (type === 'bomb') SFX.bombTick();
  else if (type === 'bonus') SFX.bonus();
  setTimeout(() => {
    const elT = tileEls.get(id);
    if (elT) {
      elT.classList.add('lt-tile--mystery-spawn');
      setTimeout(() => elT.classList.remove('lt-tile--mystery-spawn'), 700);
    }
  }, 30);
}

// Caída lenta visible de specials que aún están cayendo
const SPECIAL_FALL_INTERVAL = 380;  // ms por fila
function stepSpecialsGravity(dtMs) {
  state._specialFallAcc = (state._specialFallAcc || 0) + dtMs;
  if (state._specialFallAcc < SPECIAL_FALL_INTERVAL) return;
  state._specialFallAcc -= SPECIAL_FALL_INTERVAL;
  let moved = false;
  for (const t of state.tiles.values()) {
    if (!t._slowFalling) continue;
    // Mira si puede bajar 1 fila
    const canFall = t.cells.every(({ r, c }) => {
      if (r + 1 >= ROWS) return false;
      const occ = state.board[r + 1][c];
      return occ === null || occ === t.id;
    });
    if (canFall) {
      for (const cell of t.cells) state.board[cell.r][cell.c] = null;
      for (const cell of t.cells) cell.r += 1;
      for (const cell of t.cells) state.board[cell.r][cell.c] = t.id;
      moved = true;
    } else {
      t._slowFalling = false;  // aterrizó
      state._justLanded.add(t.id);
    }
  }
  if (moved) render();
}

function updateSpecialTileTimers() {
  const now = performance.now();
  // Ice thaw
  for (const t of state.tiles.values()) {
    if (t.special === 'ice' && t.iceUntil && now > t.iceUntil) {
      t.special = null;
      t.iceUntil = null;
      t.letter = pickWeightedLetter();
      t.level = 0;
      const elT = tileEls.get(t.id);
      if (elT) {
        elT.classList.remove('lt-tile--ice');
        elT.classList.add('lt-tile--thawed');
        setTimeout(() => elT.classList.remove('lt-tile--thawed'), 700);
      }
      SFX.iceThaw();
    }
  }
  // Bomb fuse
  for (const t of [...state.tiles.values()]) {
    if (t.special === 'bomb' && t.bombFuseEndAt && now > t.bombFuseEndAt) {
      explodeBomb(t);
    }
  }
  // Block expire
  for (const t of state.tiles.values()) {
    if (t.blockedUntil && now > t.blockedUntil) {
      t.blockedUntil = null;
      const elT = tileEls.get(t.id);
      if (elT) elT.classList.remove('lt-tile--blocked');
    }
  }
  // Blocker decay (sent by opponent in duel)
  for (const t of [...state.tiles.values()]) {
    if (t.special === 'blocker' && t.blockerUntil && now > t.blockerUntil) {
      removeTile(t);
      const elT = tileEls.get(t.id);
      if (elT) {
        elT.classList.add('lt-tile--thawed');
        setTimeout(() => elT.remove(), 400);
        tileEls.delete(t.id);
      }
      cascadeAndChain();
      render();
    }
  }
}

function explodeBomb(bombTile) {
  const bb = tileBBox(bombTile);
  // Centro de bomba
  const cr = bb.minR;
  const cc = bb.minC;
  // Bloquea vecinos 8-dir + bomba removida
  const now = performance.now();
  const lockMs = 30000;
  for (let dr = -1; dr <= 1; dr++) {
    for (let dc = -1; dc <= 1; dc++) {
      const r = cr + dr, c = cc + dc;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS) continue;
      const t = tileAt(r, c);
      if (t && t.id !== bombTile.id && !t.special) {
        t.blockedUntil = now + lockMs;
      }
    }
  }
  // Deselecciona tile bloqueado/bomba si estaba en selección
  state.selection = state.selection.filter(id => {
    const t = state.tiles.get(id);
    return t && !t.blockedUntil && t.id !== bombTile.id;
  });
  // VFX explosión
  emitBombExplosionVfx(cr, cc);
  shakeBoard('big');
  SFX.bombExplode();
  removeTile(bombTile);
  cascadeAndChain();
  render();
}

function emitBombExplosionVfx(r, c) {
  const grid = $('grid');
  if (!grid) return;
  const cx = tileLeft(c) + CELL / 2;
  const cy = tileTop(r) + CELL / 2;
  const blast = document.createElement('div');
  blast.className = 'lt-bomb-blast';
  blast.style.left = cx + 'px';
  blast.style.top = cy + 'px';
  grid.appendChild(blast);
  setTimeout(() => blast.remove(), 900);
  // Sparks radiales
  for (let i = 0; i < 16; i++) {
    const p = document.createElement('div');
    p.className = 'lt-bomb-spark';
    const angle = (i / 16) * Math.PI * 2;
    const dist = 90 + Math.random() * 50;
    p.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
    p.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
    p.style.left = cx + 'px';
    p.style.top = cy + 'px';
    grid.appendChild(p);
    setTimeout(() => p.remove(), 1000);
  }
}

// ====================== HINT SYSTEM (vibrar letra inicial de palabra posible) ======================
const HINT_IDLE_THRESHOLD = 14;   // segundos sin interactuar para activar hint
const HINT_RETRY_INTERVAL = 8;    // tras un hint, re-evalúa cada N segundos si sigue idle
const HINT_RETRY_FAIL     = 4;    // si DFS no encontró nada, retry pronto

function resetHintTimer() {
  state._idleSec = 0;
  if (state._hintTileId != null) clearHint();
}
function clearHint() {
  const elT = tileEls.get(state._hintTileId);
  if (elT) elT.classList.remove('lt-tile--hint');
  state._hintTileId = null;
}
function maybeShowHint(dtSec) {
  if (state.selection.length > 0) { resetHintTimer(); return; }
  if (state.paused || state.gameOver) return;
  state._idleSec = (state._idleSec || 0) + dtSec;
  const threshold = state._hintTileId != null ? HINT_RETRY_INTERVAL : HINT_IDLE_THRESHOLD;
  if (state._idleSec < threshold) return;
  // Busca y aplica hint
  if (state._hintTileId != null) clearHint();
  const tileId = findHintStart();
  if (tileId != null) {
    state._idleSec = 0;
    state._hintTileId = tileId;
    const elT = tileEls.get(tileId);
    if (elT) elT.classList.add('lt-tile--hint');
    SFX.hint();
  } else {
    // Reintenta más pronto si no encontró
    state._idleSec = threshold - HINT_RETRY_FAIL;
  }
}

function findHintStart() {
  if (!state.dictionary) return null;
  const tiles = [...state.tiles.values()].filter(t =>
    !t.special && !t.blockedUntil
  );
  // Shuffle
  for (let i = tiles.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
  }
  const MAX_DEPTH = 6;
  for (const startTile of tiles) {
    if (hintDfs(startTile, new Set([startTile.id]), startTile.letter, MAX_DEPTH)) {
      return startTile.id;
    }
  }
  return null;
}
function hintDfs(current, pathSet, str, maxDepth) {
  if (str.length >= 3 && isValidWordString(str)) return true;
  if (str.length >= maxDepth) return false;
  const neighbors = new Set();
  for (const cell of current.cells) {
    for (const [dr, dc] of neighborDirs()) {
      const r = cell.r + dr, c = cell.c + dc;
      if (r < 0 || r >= ROWS || c < 0 || c >= COLS) continue;
      const id = state.board[r][c];
      if (id == null) continue;
      const t = state.tiles.get(id);
      if (!t || t.id === current.id || pathSet.has(t.id) || t.special || t.blockedUntil) continue;
      neighbors.add(t);
    }
  }
  for (const next of neighbors) {
    pathSet.add(next.id);
    if (hintDfs(next, pathSet, str + next.letter, maxDepth)) return true;
    pathSet.delete(next.id);
  }
  return false;
}

// ====================== BUCLE ======================
// Freeze de gravedad mientras el jugador está formando palabra (cap 2s).
const SELECTION_FREEZE_MS = 2000;
function isSelectionFreeze() {
  if (state.selection.length === 0) { state._selectStartTs = null; return false; }
  if (state._selectStartTs == null) state._selectStartTs = performance.now();
  return performance.now() - state._selectStartTs < SELECTION_FREEZE_MS;
}

function tick(now) {
  const dt = now - state.lastTick;
  state.lastTick = now;
  pruneExpiredBuffs();
  if (state.config && !state.paused && !state.gameOver) {
    state.levelTime += dt / 1000;
    updateDangerLine();
    updateMysterySpawn(dt / 1000);
    updateBoss(dt / 1000);
    const freeze = isSelectionFreeze();
    if (!freeze) stepSpecialsGravity(dt);
    maybeShowHint(dt / 1000);
    // Update bomb fuse texts inline (sin re-render full)
    for (const t of state.tiles.values()) {
      if (t.special === 'bomb' && t.bombFuseEndAt) {
        const elT = tileEls.get(t.id);
        if (elT) {
          const fuse = elT.querySelector('.lt-tile__bomb-fuse');
          if (fuse) {
            const remain = Math.max(0, Math.ceil((t.bombFuseEndAt - performance.now()) / 1000));
            const newText = String(remain);
            if (fuse.textContent !== newText) fuse.textContent = newText;
            if (remain <= 5) fuse.classList.add('lt-tile__bomb-fuse--urgent');
          }
        }
      }
    }
    if (state.timeLeft != null) {
      state.timeLeft -= dt/1000;
      if (state.timeLeft <= 0) {
        state.timeLeft = 0; state.gameOver = true;
        state.listeners.onGameOver?.(state.score);
        render();
      }
    }
    if (state.piece && !freeze) {
      state.dropTimer += dt;
      if (state.dropTimer >= dropInterval()) {
        state.dropTimer = 0;
        if (!tryMove(1,0)) lockPiece();
        render();
      }
    }
  }
  requestAnimationFrame(tick);
}

// ====================== RENDER (persistente con VFX) ======================
const $ = (id) => document.getElementById(id);
const tileEls = new Map();   // tileId → HTMLElement
let pieceEls = [];           // elementos de la pieza activa
let ghostEls = [];           // celdas ghost que muestran dónde caerá

state._justLanded = new Set();
state._justMerged = new Set();
state._justPushed = new Set();
state._justSlammed = new Set();
state._slamLanding = false;
state._shockwaves = [];
state._absorbs = [];
state._comboBanner = 0;
state.activeBuffs = [];
state.nextWildcard = false;

function tileLeft(c) { return PAD + c * (CELL + GAP); }
function tileTop(r)  { return PAD + r * (CELL + GAP); }

// ====================== LETRIS (Balatro cards) ======================
function letrisCtx(extra = {}) {
  return {
    state,
    intensity: getIntensity(),
    ...extra,
  };
}
function applyLetrisScore(basePts, ctx) {
  let pts = basePts;
  const triggered = [];
  for (const card of state.letris) {
    if (typeof card.scoreMul === 'function') {
      const mul = card.scoreMul(ctx);
      if (mul !== 1) { pts *= mul; triggered.push(card.id); }
    }
    if (typeof card.scoreFlat === 'function') {
      const f = card.scoreFlat(ctx);
      if (f) { pts += f; triggered.push(card.id); }
    }
  }
  if (triggered.length) state._letrisTriggered = triggered;
  return Math.round(pts);
}
function applyLetrisCoinFlat(ctx) {
  let bonus = 0;
  for (const card of state.letris) {
    if (typeof card.coinFlat === 'function') bonus += card.coinFlat(ctx) || 0;
  }
  return bonus;
}
function applyLetrisGraceFlat(ctx) {
  let bonus = 0;
  for (const card of state.letris) {
    if (typeof card.graceFlat === 'function') bonus += card.graceFlat(ctx) || 0;
  }
  return bonus;
}
function applyLetrisGravityMul() {
  let mul = 1;
  for (const card of state.letris) if (typeof card.gravityMul === 'function') mul *= card.gravityMul();
  return mul;
}
function applyLetrisDangerMul() {
  let mul = 1;
  for (const card of state.letris) if (typeof card.dangerMul === 'function') mul *= card.dangerMul();
  return mul;
}
function applyLetrisDropMul(ctx) {
  let mul = 1;
  for (const card of state.letris) if (typeof card.dropMul === 'function') mul *= card.dropMul(ctx);
  return mul;
}
function rollLetrisDrop(wordLen, isAutoWord) {
  // Probabilidad base por longitud
  const baseByLen = { 3: 0.02, 4: 0.06, 5: 0.14, 6: 0.28 };
  let p = wordLen >= 7 ? 0.5 : (baseByLen[wordLen] || 0.02);
  // Aplica modificadores Letris
  p *= applyLetrisDropMul({ wordLen });
  if (Math.random() > p) return null;
  // Selecciona rareza por peso, luego carta
  const totalW = Object.values(LETRIS_RARITY).reduce((s, r) => s + r.weight, 0);
  let roll = Math.random() * totalW;
  let rarity = 'common';
  for (const [k, v] of Object.entries(LETRIS_RARITY)) {
    if (roll < v.weight) { rarity = k; break; }
    roll -= v.weight;
  }
  const pool = LETRIS_POOL.filter(c => c.rarity === rarity);
  if (!pool.length) return null;
  // Evita duplicar cards ya equipadas
  const eligible = pool.filter(c => !state.letris.some(eq => eq.id === c.id));
  const winner = (eligible.length ? eligible : pool)[Math.floor(Math.random() * (eligible.length ? eligible.length : pool.length))];
  return winner;
}
function pickLetrisCard({ allowLegendary = false } = {}) {
  const rarityWeights = Object.entries(LETRIS_RARITY).map(([id, r]) => ({
    id,
    weight: (id === 'rare' || id === 'legendary')
      ? (id === 'legendary' && !allowLegendary ? Math.max(1, r.weight * 0.35) : r.weight) * (1 + (state.config?.draftLuck || 0) * 0.45)
      : r.weight,
  }));
  const totalW = rarityWeights.reduce((s, r) => s + r.weight, 0);
  let roll = state.rng() * totalW;
  let rarity = 'common';
  for (const r of rarityWeights) {
    if (roll < r.weight) { rarity = r.id; break; }
    roll -= r.weight;
  }
  const pool = LETRIS_POOL.filter(c => c.rarity === rarity);
  const eligible = pool.filter(c => !state.letris.some(eq => eq.id === c.id));
  const list = eligible.length ? eligible : pool;
  return list[Math.floor(state.rng() * list.length)];
}

function openLetrisChoiceModal() {
  if (state.config?.duelMode || document.querySelector('.lt-letris-modal')) return;
  const seen = new Set();
  const options = [];
  let safety = 0;
  while (options.length < 3 && safety++ < 30) {
    const card = pickLetrisCard({ allowLegendary: state.level >= 10 });
    if (!card || seen.has(card.id)) continue;
    seen.add(card.id);
    options.push(card);
  }
  if (!options.length) return;
  state.paused = true;
  SFX.letrisDrop();
  render();

  const overlay = document.createElement('div');
  overlay.className = 'lt-letris-modal lt-letris-modal--choice';
  const inner = document.createElement('div');
  inner.className = 'lt-letris-modal__inner lt-letris-modal__inner--choice';
  const title = document.createElement('div');
  title.className = 'lt-letris-modal__title';
  title.textContent = 'ELIGE UNA LETRIS';
  const sub = document.createElement('div');
  sub.className = 'lt-letris-modal__subtitle';
  sub.textContent = 'Recompensa de nivel. Gratis.';
  const row = document.createElement('div');
  row.className = 'lt-letris-choice-row';
  for (const card of options) {
    const rarity = LETRIS_RARITY[card.rarity];
    const btn = document.createElement('button');
    btn.className = 'lt-letris-card lt-letris-card--xl lt-letris-choice';
    btn.style.setProperty('--card-color', card.color);
    btn.style.setProperty('--rarity-bar', rarity.glow);
    btn.innerHTML = `
      <div class="lt-letris-card__rarity">${rarity.label}</div>
      <div class="lt-letris-card__icon">${card.icon}</div>
      <div class="lt-letris-card__name">${card.name}</div>
      <div class="lt-letris-card__desc">${card.desc}</div>
    `;
    btn.onclick = () => equipFreeLetris(card, overlay);
    row.appendChild(btn);
  }
  const skip = document.createElement('button');
  skip.className = 'lt-btn lt-btn--ghost';
  skip.textContent = 'SALTAR';
  skip.onclick = () => closeLetrisModal(overlay);
  inner.appendChild(title);
  inner.appendChild(sub);
  inner.appendChild(row);
  inner.appendChild(skip);
  overlay.appendChild(inner);
  document.body.appendChild(overlay);
}

function equipFreeLetris(card, overlay) {
  const maxSlots = LETRIS_MAX_SLOTS + (state.config?.letrisExtraSlot || 0);
  if (state.letris.length < maxSlots) {
    state.letris.push(card);
    state.listeners.onLetrisChange?.(state.letris);
    SFX.letrisEquip();
    closeLetrisModal(overlay);
    return;
  }
  openReplaceSubmodal(card, overlay, { free: true });
}

function pulseLetrisCards(ids) {
  if (!ids?.length) return;
  for (const id of ids) {
    const card = document.querySelector(`.lt-letris-card[data-id="${id}"]`);
    if (!card) continue;
    card.classList.remove('lt-letris-card--pulse');
    void card.offsetWidth;
    card.classList.add('lt-letris-card--pulse');
    setTimeout(() => card.classList.remove('lt-letris-card--pulse'), 700);
  }
}
function openLetrisDropModal(card) {
  if (document.querySelector('.lt-letris-modal')) return;
  state.paused = true;
  state.pendingLetris = card;
  SFX.letrisDrop();
  render();

  const rarity = LETRIS_RARITY[card.rarity];
  const cost = letrisCostFor(card.rarity, state.level);
  const overlay = document.createElement('div');
  overlay.className = 'lt-letris-modal';
  overlay.style.setProperty('--rarity-glow', rarity.glow);

  const cardEl = document.createElement('div');
  cardEl.className = 'lt-letris-card lt-letris-card--xl lt-letris-card--flip';
  cardEl.style.setProperty('--card-color', card.color);
  cardEl.style.setProperty('--rarity-bar', rarity.glow);
  cardEl.innerHTML = `
    <div class="lt-letris-card__back"></div>
    <div class="lt-letris-card__face">
      <div class="lt-letris-card__rarity">${rarity.label}</div>
      <div class="lt-letris-card__icon">${card.icon}</div>
      <div class="lt-letris-card__name">${card.name}</div>
      <div class="lt-letris-card__desc">${card.desc}</div>
    </div>
  `;

  const actions = document.createElement('div');
  actions.className = 'lt-letris-modal__actions';
  const equipBtn = document.createElement('button');
  equipBtn.className = 'lt-btn lt-btn--primary';
  const canAfford = state.score >= cost;
  equipBtn.disabled = !canAfford;
  equipBtn.innerHTML = `EQUIPAR <span class="lt-letris-cost">${cost} pts</span>`;
  equipBtn.onclick = () => onEquipDecision(card, cost, overlay);
  const discardBtn = document.createElement('button');
  discardBtn.className = 'lt-btn lt-btn--ghost';
  discardBtn.textContent = 'DESCARTAR';
  discardBtn.onclick = () => closeLetrisModal(overlay);

  actions.appendChild(equipBtn);
  actions.appendChild(discardBtn);

  const title = document.createElement('div');
  title.className = 'lt-letris-modal__title';
  title.textContent = '¡LETRIS APARECE!';

  const inner = document.createElement('div');
  inner.className = 'lt-letris-modal__inner';
  inner.appendChild(title);
  inner.appendChild(cardEl);
  inner.appendChild(actions);
  overlay.appendChild(inner);
  document.body.appendChild(overlay);
}

function closeLetrisModal(overlay) {
  state.pendingLetris = null;
  overlay.remove();
  // Cierra cualquier sub-modal de slot-replace
  document.querySelectorAll('.lt-letris-replace').forEach(e => e.remove());
  state.paused = false;
  render();
}

function onEquipDecision(card, cost, overlay) {
  if (state.score < cost) return;
  state.score -= cost;
  state.levelProgress.points = state.score;
  const maxSlots = LETRIS_MAX_SLOTS + (state.config?.letrisExtraSlot || 0);
  if (state.letris.length < maxSlots) {
    state.letris.push(card);
    state.listeners.onLetrisChange?.(state.letris);
    SFX.letrisEquip();
    closeLetrisModal(overlay);
    return;
  }
  // Slots llenos: pregunta cuál sustituir
  openReplaceSubmodal(card, overlay);
}

function openReplaceSubmodal(newCard, parentOverlay, opts = {}) {
  const sub = document.createElement('div');
  sub.className = 'lt-letris-replace';
  const title = document.createElement('div');
  title.className = 'lt-letris-replace__title';
  title.textContent = 'SLOTS LLENOS — ¿CUÁL SUSTITUYES?';
  sub.appendChild(title);
  const row = document.createElement('div');
  row.className = 'lt-letris-replace__row';
  for (let i = 0; i < state.letris.length; i++) {
    const old = state.letris[i];
    const slot = document.createElement('button');
    slot.className = 'lt-letris-card lt-letris-card--md';
    slot.style.setProperty('--card-color', old.color);
    slot.style.setProperty('--rarity-bar', LETRIS_RARITY[old.rarity].glow);
    slot.innerHTML = `
      <div class="lt-letris-card__rarity">${LETRIS_RARITY[old.rarity].label}</div>
      <div class="lt-letris-card__icon">${old.icon}</div>
      <div class="lt-letris-card__name">${old.name}</div>
      <div class="lt-letris-card__desc">${old.desc}</div>
    `;
    slot.onclick = () => {
      state.letris[i] = newCard;
      state.listeners.onLetrisChange?.(state.letris);
      closeLetrisModal(parentOverlay);
    };
    row.appendChild(slot);
  }
  sub.appendChild(row);
  const cancel = document.createElement('button');
  cancel.className = 'lt-btn lt-btn--ghost';
  cancel.textContent = opts.free ? 'CANCELAR' : 'CANCELAR (devolver pts)';
  cancel.onclick = () => {
    if (!opts.free) {
      const cost = letrisCostFor(newCard.rarity, state.level);
      state.score += cost;
      state.levelProgress.points = state.score;
    }
    sub.remove();
  };
  sub.appendChild(cancel);
  parentOverlay.appendChild(sub);
}

function flashLetrisOnBoard(ids, originR, originC) {
  if (!ids?.length) return;
  for (const id of ids) {
    const card = LETRIS_POOL.find(c => c.id === id);
    if (!card) continue;
    const fx = document.createElement('div');
    fx.className = 'lt-letris-fx';
    fx.style.color = card.color;
    fx.textContent = card.icon + ' ' + card.name;
    const grid = $('grid');
    if (!grid) continue;
    fx.style.left = (tileLeft(originC) + CELL / 2) + 'px';
    fx.style.top = (tileTop(originR) + CELL / 2) + 'px';
    grid.appendChild(fx);
    setTimeout(() => fx.remove(), 1100);
  }
}

// ====================== INTENSIDAD ======================
// Sube +1 cada 30s del nivel hasta 10. Reset al subir nivel pero arranca desde nivel-1 base.
// Reducible con palabras largas (alivio).
function getIntensity() {
  if (state.config?.gravityScales === false) return 0;
  const baseFromLevel = Math.max(0, state.level - 1);
  const fromTime = Math.floor(state.levelTime / 30);
  const relief = state._intensityRelief || 0;
  const fill = boardFillRatio();
  const panicRelief = fill > 0.66 ? 2 : fill > 0.58 ? 1 : 0;
  state._directorRelief = panicRelief;
  return Math.max(0, Math.min(10, baseFromLevel + fromTime - relief - panicRelief));
}

// ====================== COIN VFX ======================
function spawnCoinBurstAtBoardPos(r, c, amount) {
  const grid = $('grid');
  const counter = document.getElementById('coin-counter');
  if (!grid || !counter || amount <= 0) return;
  SFX.coinGain();
  const gridRect = grid.getBoundingClientRect();
  const counterRect = counter.getBoundingClientRect();
  const sx = gridRect.left + tileLeft(c) + CELL / 2;
  const sy = gridRect.top + tileTop(r) + CELL / 2;
  const tx = counterRect.left + counterRect.width / 2;
  const ty = counterRect.top + counterRect.height / 2;
  const N = Math.min(12, Math.max(1, Math.ceil(amount / 2)));
  for (let i = 0; i < N; i++) {
    const coin = document.createElement('div');
    coin.className = 'lt-coin-fly';
    coin.textContent = '◈';
    coin.style.left = sx + 'px';
    coin.style.top = sy + 'px';
    const jitter = 26;
    const dx = tx - sx + (Math.random() - 0.5) * jitter;
    const dy = ty - sy + (Math.random() - 0.5) * jitter;
    coin.style.setProperty('--dx', dx + 'px');
    coin.style.setProperty('--dy', dy + 'px');
    coin.style.animationDelay = (i * 38) + 'ms';
    document.body.appendChild(coin);
    setTimeout(() => coin.remove(), 1200 + i * 38);
  }
}

function spawnCoinBurstAtElement(el, amount) {
  const grid = $('grid');
  if (!grid || !el) return spawnCoinBurstAtBoardPos(ROWS / 2, COLS / 2, amount);
  const gridRect = grid.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const cx = r.left + r.width / 2 - gridRect.left;
  const cy = r.top + r.height / 2 - gridRect.top;
  // convertir px a r,c aprox
  const cc = (cx - PAD) / (CELL + GAP);
  const rr = (cy - PAD) / (CELL + GAP);
  spawnCoinBurstAtBoardPos(rr, cc, amount);
}

function applyTileStyle(el, tile) {
  const bb = tileBBox(tile);
  const minR = Math.max(0, Math.min(ROWS - bb.h, bb.minR));
  const minC = Math.max(0, Math.min(COLS - bb.w, bb.minC));
  el.style.left = tileLeft(minC) + 'px';
  el.style.top  = tileTop(minR) + 'px';
  el.style.width  = (bb.w * CELL + (bb.w-1) * GAP) + 'px';
  el.style.height = (bb.h * CELL + (bb.h-1) * GAP) + 'px';
  const s = LEVEL_STYLES[tile.level];
  el.style.background = s.bg;
  el.style.color = s.fg;
  const avg = (bb.w + bb.h) / 2;
  el.style.fontSize = Math.round(CELL * 0.55 * (1 + 0.22 * (avg-1))) + 'px';
  el.classList.toggle('lt-tile--max', tile.level === MAX_LEVEL);
  el.classList.toggle('lt-tile--selected', state.selection.includes(tile.id));
  el.dataset.level = tile.level;
}

function setTileContent(el, tile) {
  el.innerHTML = '';
  const span = document.createElement('span');
  span.className = 'lt-tile__letter';
  span.textContent = tile.letter;
  el.appendChild(span);
  if (tile.level > 0) {
    const lvl = document.createElement('span');
    lvl.className = 'lt-tile__level';
    lvl.textContent = '•'.repeat(tile.level);
    el.appendChild(lvl);
  }
  // Specials overlay
  if (tile.special && tile.special !== 'mystery' && tile.special !== 'blocker') {
    const def = SPECIAL_TYPES[tile.special];
    const badge = document.createElement('span');
    badge.className = 'lt-tile__special-badge lt-tile__special-badge--' + tile.special;
    badge.textContent = def?.icon || '';
    el.appendChild(badge);
    if (tile.special === 'bomb' && tile.bombFuseEndAt) {
      const remain = Math.max(0, Math.ceil((tile.bombFuseEndAt - performance.now()) / 1000));
      const fuse = document.createElement('span');
      fuse.className = 'lt-tile__bomb-fuse';
      fuse.textContent = remain;
      if (remain <= 5) fuse.classList.add('lt-tile__bomb-fuse--urgent');
      el.appendChild(fuse);
    }
  }
}

function ensureTileEl(tile) {
  let el = tileEls.get(tile.id);
  let isNew = false;
  if (!el) {
    el = document.createElement('button');
    el.className = 'lt-tile lt-tile-abs';
    el.dataset.tileId = tile.id;
    el.tabIndex = -1;
    el.addEventListener('contextmenu', (e) => e.preventDefault());
    tileEls.set(tile.id, el);
    isNew = true;
  }
  applyTileStyle(el, tile);
  setTileContent(el, tile);
  el.classList.toggle('lt-tile--mystery', !!tile.mystery);
  el.classList.toggle('lt-tile--bonus', tile.special === 'bonus');
  el.classList.toggle('lt-tile--ice', tile.special === 'ice');
  el.classList.toggle('lt-tile--bomb', tile.special === 'bomb');
  el.classList.toggle('lt-tile--blocker', tile.special === 'blocker');
  el.classList.toggle('lt-tile--hint', state._hintTileId === tile.id);
  const isBlocked = tile.blockedUntil && performance.now() < tile.blockedUntil;
  el.classList.toggle('lt-tile--blocked', !!isBlocked);
  if (isNew) {
    el.style.transition = 'none';
    $('grid').appendChild(el);
    void el.offsetHeight;
    el.style.transition = '';
  }
  // Bounce on land (normal o slam por hard drop)
  if (state._justLanded.has(tile.id) || state._justSlammed.has(tile.id)) {
    const isSlam = state._justSlammed.has(tile.id);
    const cls = isSlam ? 'lt-tile--slammed' : 'lt-tile--landed';
    const dur = isSlam ? 580 : 450;
    el.classList.remove('lt-tile--landed', 'lt-tile--slammed');
    void el.offsetWidth;
    const delay = isNew ? 0 : 180;
    setTimeout(() => {
      el.classList.add(cls);
      setTimeout(() => el.classList.remove(cls), dur);
    }, delay);
  }
  if (state._justMerged.has(tile.id)) {
    el.classList.remove('lt-tile--merged');
    void el.offsetWidth;
    el.classList.add('lt-tile--merged');
    setTimeout(() => el.classList.remove('lt-tile--merged'), 550);
  }
  if (state._justPushed.has(tile.id)) {
    el.classList.remove('lt-tile--pushed');
    void el.offsetWidth;
    el.classList.add('lt-tile--pushed');
    setTimeout(() => el.classList.remove('lt-tile--pushed'), 380);
  }
}

function emitParticles(r, c, color) {
  const grid = $('grid');
  const cx = tileLeft(c) + CELL/2;
  const cy = tileTop(r) + CELL/2;
  const colors = [color, '#06D6A0', '#FFFFFF', '#FFD166'];
  for (let i = 0; i < 8; i++) {
    const p = document.createElement('div');
    p.className = 'lt-particle';
    p.style.background = colors[i % colors.length];
    p.style.left = cx + 'px';
    p.style.top = cy + 'px';
    const angle = (i / 8) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 25 + Math.random() * 35;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist + 30 + Math.random() * 20;
    const rot = (Math.random() - 0.5) * 720;
    p.style.setProperty('--dx', dx + 'px');
    p.style.setProperty('--dy', dy + 'px');
    p.style.setProperty('--rot', rot + 'deg');
    grid.appendChild(p);
    setTimeout(() => p.remove(), 800);
  }
}

function emitScorePop(r, c, points) {
  const grid = $('grid');
  const el = document.createElement('div');
  el.className = 'lt-score-pop';
  el.textContent = '+' + points;
  el.style.left = (tileLeft(c) + CELL/2) + 'px';
  el.style.top = tileTop(r) + 'px';
  grid.appendChild(el);
  setTimeout(() => el.remove(), 1100);
}

function emitWordImpactVfx(r, c, word, points) {
  const grid = $('grid');
  if (!grid) return;
  const len = word.length;
  const tier = len >= 7 ? 'epic' : len >= 5 ? 'long' : len >= 4 ? 'mid' : 'short';
  const cx = tileLeft(c) + CELL / 2;
  const cy = tileTop(r) + CELL / 2;
  const impact = document.createElement('div');
  impact.className = `lt-word-impact lt-word-impact--${tier}`;
  impact.style.left = cx + 'px';
  impact.style.top = cy + 'px';
  impact.textContent = len >= 5 ? word : '';
  grid.appendChild(impact);
  setTimeout(() => impact.remove(), 900);
  if (len >= 6) flashBoardChromatic(Math.min(6, len - 3));
  if (len >= 7) {
    const badge = document.createElement('div');
    badge.className = 'lt-word-finisher';
    badge.innerHTML = `<strong>${word}</strong><span>${len} LETRAS · +${points}</span>`;
    grid.appendChild(badge);
    setTimeout(() => badge.remove(), 1200);
  }
}

function updateWordStreakVfx(wordLen) {
  const now = performance.now();
  if (!state._wordStreak || now - state._wordStreak.ts > 5200) {
    state._wordStreak = { count: 0, ts: now };
  }
  state._wordStreak.count++;
  state._wordStreak.ts = now;
  if (wordLen >= 5) state._wordStreak.count++;
  const count = state._wordStreak.count;
  if (count < 3) return;
  const grid = $('grid');
  if (!grid) return;
  const meter = document.createElement('div');
  meter.className = 'lt-word-streak';
  meter.innerHTML = `<span>RACHA</span><strong>x${Math.min(9, count)}</strong>`;
  grid.appendChild(meter);
  setTimeout(() => meter.remove(), 900);
}

function emitAbsorbVfx({sr, sc, tr, tc, letter, color, fg, bonus}) {
  const grid = $('grid');
  const sx = tileLeft(sc) + CELL/2;
  const sy = tileTop(sr) + CELL/2;
  const tx = tileLeft(tc) + CELL/2;
  const ty = tileTop(tr) + CELL/2;
  const el = document.createElement('div');
  el.className = 'lt-absorb-vfx';
  el.textContent = letter;
  el.style.background = color;
  el.style.color = fg;
  el.style.left = sx + 'px';
  el.style.top = sy + 'px';
  el.style.setProperty('--tx', (tx - sx) + 'px');
  el.style.setProperty('--ty', (ty - sy) + 'px');
  grid.appendChild(el);
  setTimeout(() => el.remove(), 520);
  // mini score pop al destino
  const pop = document.createElement('div');
  pop.className = 'lt-score-pop lt-score-pop--absorb';
  pop.textContent = '+' + bonus;
  pop.style.left = tx + 'px';
  pop.style.top = (ty - 8) + 'px';
  grid.appendChild(pop);
  setTimeout(() => pop.remove(), 1100);
}

function emitComboBanner(count) {
  const grid = $('grid');
  const el = document.createElement('div');
  el.className = 'lt-combo-banner';
  el.innerHTML = `COMBO <strong>×${count}</strong>`;
  grid.appendChild(el);
  setTimeout(() => el.remove(), 1300);
}

function emitWordCombo(word, pts) {
  const grid = $('grid');
  const el = document.createElement('div');
  el.className = 'lt-word-combo';
  el.innerHTML = `<div class="lt-word-combo__word">${word}</div><div class="lt-word-combo__pts">+${pts} ★ AUTO</div>`;
  grid.appendChild(el);
  setTimeout(() => el.remove(), 1500);
}

// Escanea filas/columnas buscando secuencias contiguas que formen palabras del diccionario.
// Devuelve array {tiles, word} ordenado por longitud descendente (mejor primero).
function findAutoWords(originCells) {
  if (!state.dictionary) return [];
  const found = [];
  const usedTileIds = new Set();

  // Vecindad: solo aceptar palabras que toquen al menos una celda dentro del radio
  // alrededor de las celdas de origen (palabra recién limpiada).
  const RADIUS = 2;
  const nearSet = new Set();
  if (originCells && originCells.length) {
    for (const { r, c } of originCells) {
      for (let dr = -RADIUS; dr <= RADIUS; dr++) {
        for (let dc = -RADIUS; dc <= RADIUS; dc++) {
          nearSet.add((r + dr) + ',' + (c + dc));
        }
      }
    }
  }
  function tileNear(t) {
    if (!nearSet.size) return true;  // sin restricción si no hay origen
    return t.cells.some(({ r, c }) => nearSet.has(r + ',' + c));
  }

  function check(tiles) {
    if (tiles.length < 3) return;
    for (let len = tiles.length; len >= 3; len--) {
      for (let start = 0; start + len <= tiles.length; start++) {
        const sub = tiles.slice(start, start + len);
        if (sub.some(t => usedTileIds.has(t.id))) continue;
        if (!sub.some(tileNear)) continue;  // gate por vecindad
        const word = sub.map(t => t.letter).join('');
        if (isValidWordString(word)) {
          found.push({ tiles: sub, word });
          sub.forEach(t => usedTileIds.add(t.id));
          return;
        }
      }
    }
  }

  // horizontal
  for (let r = 0; r < ROWS; r++) {
    let run = []; let lastId = null;
    for (let c = 0; c < COLS; c++) {
      const t = tileAt(r, c);
      if (t && !t.mystery) {
        if (t.id !== lastId) { run.push(t); lastId = t.id; }
      } else {
        check(run); run = []; lastId = null;
      }
    }
    check(run);
  }
  // vertical
  for (let c = 0; c < COLS; c++) {
    let run = []; let lastId = null;
    for (let r = 0; r < ROWS; r++) {
      const t = tileAt(r, c);
      if (t && !t.mystery) {
        if (t.id !== lastId) { run.push(t); lastId = t.id; }
      } else {
        check(run); run = []; lastId = null;
      }
    }
    check(run);
  }
  found.sort((a, b) => b.tiles.length - a.tiles.length);
  return found;
}

function processAutoWords(originCells) {
  let chain = 0;
  let currentOrigin = originCells;
  while (chain < 5) {
    const autoWords = findAutoWords(currentOrigin);
    if (autoWords.length === 0) break;
    const aw = autoWords[0];
    // Próxima vecindad = celdas de la palabra que se va a limpiar (sigue cascada local)
    currentOrigin = aw.tiles.flatMap(t => t.cells.map(c => ({ r: c.r, c: c.c })));
    chain++;
    const tiles = aw.tiles;
    const sum = tiles.reduce((acc, t) => {
      return acc + letterValue(t.letter) * (t.level + 1);
    }, 0);
    let pts = Math.round(sum * tiles.length * 2 * levelMultiplier());
    pts = Math.round(pts * (1 + chain * 0.3));
    state.score += pts;
    if (state.score > state.bestThisRun) state.bestThisRun = state.score;
    state.history.unshift({
      word: aw.word + ' ★',
      points: pts,
      level: Math.max(0, ...tiles.map(t => t.level || 0)),
    });
    state.history = state.history.slice(0, 12);
    emitWordCombo(aw.word, pts);
    for (const t of tiles) {
      for (const cell of t.cells) emitParticles(cell.r, cell.c, LEVEL_STYLES[t.level].bg);
      removeTile(t);
    }
    cascadeAndChain();
    state.listeners.onWord?.({ word: aw.word, points: pts, level: state.level, usedFusion: false });
    awardDangerGrace(aw.word.length);
  }
  if (chain > 0) state.levelProgress.points = state.score;
  return chain;
}

function renderTiles() {
  for (const tile of state.tiles.values()) ensureTileEl(tile);
  for (const [id, el] of tileEls) {
    if (!state.tiles.has(id)) {
      el.remove();
      tileEls.delete(id);
    }
  }
  for (const sw of state._shockwaves) emitShockwave(sw);
  state._shockwaves = [];
  for (const a of state._absorbs) emitAbsorbVfx(a);
  state._absorbs = [];
  if (state._comboBanner >= 2) {
    emitComboBanner(state._comboBanner);
    if (state._comboBanner >= 4) shakeBoard('big');
    else shakeBoard('normal');
    flashBoardChromatic(state._comboBanner);
    SFX.combo(state._comboBanner);
    state._comboBanner = 0;
  }
  state._justLanded.clear();
  state._justMerged.clear();
  state._justPushed.clear();
  state._justSlammed.clear();
  renderPieceCells();
  renderSelectionPath();
}

function renderSelectionPath() {
  const grid = $('grid');
  if (!grid) return;
  const old = grid.querySelector('.lt-selpath');
  if (old) old.remove();
  if (state.selection.length === 0) return;

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'lt-selpath');
  const w = COLS * (CELL + GAP) + GAP + PAD * 2;
  const h = ROWS * (CELL + GAP) + GAP + PAD * 2;
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('width', w);
  svg.setAttribute('height', h);

  const pts = [];
  for (const id of state.selection) {
    const t = state.tiles.get(id);
    if (!t) continue;
    const bb = tileBBox(t);
    const cx = tileLeft(bb.minC) + (bb.w * (CELL+GAP) - GAP) / 2;
    const cy = tileTop(bb.minR) + (bb.h * (CELL+GAP) - GAP) / 2;
    pts.push([cx, cy]);
  }

  // polyline (línea entre tiles)
  if (pts.length >= 2) {
    const poly = document.createElementNS(NS, 'polyline');
    poly.setAttribute('points', pts.map(([x,y]) => `${x},${y}`).join(' '));
    poly.setAttribute('class', 'lt-selpath__line');
    svg.appendChild(poly);
    // arrow head al último
    const [x1, y1] = pts[pts.length - 2];
    const [x2, y2] = pts[pts.length - 1];
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const arrowSize = 11;
    const ax1 = x2 - arrowSize * Math.cos(angle - Math.PI/6);
    const ay1 = y2 - arrowSize * Math.sin(angle - Math.PI/6);
    const ax2 = x2 - arrowSize * Math.cos(angle + Math.PI/6);
    const ay2 = y2 - arrowSize * Math.sin(angle + Math.PI/6);
    const arrow = document.createElementNS(NS, 'polygon');
    arrow.setAttribute('points', `${x2},${y2} ${ax1},${ay1} ${ax2},${ay2}`);
    arrow.setAttribute('class', 'lt-selpath__arrow');
    svg.appendChild(arrow);
  }

  // números de orden encima de cada tile
  pts.forEach(([x, y], i) => {
    const circle = document.createElementNS(NS, 'circle');
    circle.setAttribute('cx', x);
    circle.setAttribute('cy', y - 14);
    circle.setAttribute('r', 9);
    circle.setAttribute('class', 'lt-selpath__num-bg');
    svg.appendChild(circle);
    const text = document.createElementNS(NS, 'text');
    text.setAttribute('x', x);
    text.setAttribute('y', y - 11);
    text.setAttribute('class', 'lt-selpath__num');
    text.setAttribute('text-anchor', 'middle');
    text.textContent = (i + 1).toString();
    svg.appendChild(text);
  });

  grid.appendChild(svg);
}

function emitShockwave({ r, c, w, h }) {
  const grid = $('grid');
  const el = document.createElement('div');
  el.className = 'lt-shockwave';
  const left = tileLeft(c) + (CELL/2);
  const top  = tileTop(r) + (CELL/2);
  el.style.left = left + 'px';
  el.style.top = top + 'px';
  const baseSize = Math.max(w, h) * (CELL + GAP);
  el.style.width = baseSize + 'px';
  el.style.height = baseSize + 'px';
  el.style.marginLeft = (-baseSize/2) + 'px';
  el.style.marginTop = (-baseSize/2) + 'px';
  grid.appendChild(el);
  setTimeout(() => el.remove(), 700);
}

let _lastPieceSig = null;
function pieceSig(p) {
  if (!p) return null;
  // r,c,rotación (cells signature) → si igual, no rebuild
  return p.r + ':' + p.c + ':' + p.cells.map(c => `${c.dr},${c.dc},${c.letter},${c.level}`).join('|');
}
function renderPieceCells() {
  const sig = pieceSig(state.piece);
  if (sig === _lastPieceSig && pieceEls.length > 0) return;  // sin cambios → skip rebuild
  _lastPieceSig = sig;
  // borra pieza previa + ghost previo
  pieceEls.forEach(el => el.remove());
  pieceEls = [];
  ghostEls.forEach(el => el.remove());
  ghostEls = [];
  if (!state.piece) return;
  const p = state.piece;
  // Calcula fila de aterrizaje simulando bajadas
  let landR = p.r;
  while (pieceCanFitAt(p, landR + 1, p.c)) landR++;
  // Ghost cells (sólo si la pieza no está ya en el suelo)
  if (landR > p.r) {
    for (const cell of p.cells) {
      const g = document.createElement('div');
      g.className = 'lt-tile lt-tile-abs lt-tile--ghost';
      g.style.transition = 'none';
      const fake = { letter: cell.letter, level: cell.level, cells: [{r: landR + cell.dr, c: p.c + cell.dc}] };
      applyTileStyle(g, fake);
      g.innerHTML = '';
      $('grid').appendChild(g);
      ghostEls.push(g);
    }
  }
  for (const cell of p.cells) {
    const el = document.createElement('div');
    el.className = 'lt-tile lt-tile-abs lt-tile--falling';
    el.style.transition = 'none';
    const fakeTile = { letter: cell.letter, level: cell.level, cells: [{r: p.r + cell.dr, c: p.c + cell.dc}] };
    applyTileStyle(el, fakeTile);
    setTileContent(el, fakeTile);
    $('grid').appendChild(el);
    pieceEls.push(el);
  }
}

function fmtTime(s) {
  if (s == null) return '—';
  const m = Math.floor(s/60), ss = Math.floor(s%60);
  return `${m}:${String(ss).padStart(2,'0')}`;
}

let _scoreDisplay = 0;
let _scoreAnimRaf = 0;
function animateScoreTo(target) {
  cancelAnimationFrame(_scoreAnimRaf);
  const start = _scoreDisplay;
  const delta = target - start;
  if (delta === 0) return;
  const dur = Math.min(900, 250 + Math.log2(Math.max(2, Math.abs(delta))) * 80);
  const t0 = performance.now();
  function step(now) {
    const t = Math.min(1, (now - t0) / dur);
    const eased = 1 - Math.pow(1 - t, 3);
    _scoreDisplay = Math.round(start + delta * eased);
    const sEl = $('score');
    if (sEl) sEl.textContent = String(_scoreDisplay).padStart(6, '0');
    if (t < 1) _scoreAnimRaf = requestAnimationFrame(step);
    else _scoreDisplay = target;
  }
  _scoreAnimRaf = requestAnimationFrame(step);
  // pulso visual del score
  const sEl = $('score');
  if (sEl && delta > 0) {
    sEl.classList.remove('lt-score--pulse');
    void sEl.offsetWidth;
    sEl.classList.add('lt-score--pulse');
    setTimeout(() => sEl.classList.remove('lt-score--pulse'), 480);
  }
}

function renderTopAndPanels() {
  $('btn-pause').textContent = state.paused ? '▶' : '‖';
  $('btn-pause').classList.toggle('on', state.paused);
  // Score con count-up animado en lugar de set directo
  if (_scoreDisplay !== state.score) animateScoreTo(state.score);
  $('best').textContent = String(state.bestThisRun).padStart(6, '0');
  // active buffs chips
  const buffsEl = $('active-buffs');
  if (buffsEl) {
    buffsEl.innerHTML = '';
    const now = performance.now();
    for (const b of state.activeBuffs) {
      const chip = document.createElement('div');
      chip.className = 'lt-buff-chip lt-buff-chip--' + (b.kind || 'buff');
      chip.style.borderColor = '#000';
      chip.style.background = b.color;
      chip.style.color = b.kind === 'debuff' ? '#fff' : '#000';
      let extra = '';
      if (b.usesLeft) extra = `×${b.usesLeft}`;
      else if (b.expiresAt) extra = Math.max(0, Math.ceil((b.expiresAt - now) / 1000)) + 's';
      chip.innerHTML = `<span class="lt-buff-icon">${b.icon}</span><span class="lt-buff-extra">${extra}</span>`;
      chip.title = b.name + ' — ' + b.desc;
      buffsEl.appendChild(chip);
    }
  }

  const wordDisp = $('word-display');
  document.body.classList.toggle('lt-has-sel', state.selection.length > 0);
  wordDisp.innerHTML = '';
  if (state.selection.length === 0) {
    const e = document.createElement('span');
    e.className = 'lt-word-empty'; e.textContent = '— — —';
    wordDisp.appendChild(e);
  } else {
    state.selection.forEach(id => {
      const t = state.tiles.get(id); if (!t) return;
      const s = LEVEL_STYLES[t.level];
      const chip = document.createElement('span');
      chip.className = 'lt-word-chip';
      chip.style.background = s.bg; chip.style.color = s.fg;
      chip.textContent = t.letter;
      wordDisp.appendChild(chip);
    });
  }
  $('btn-send').disabled = state.selection.length < 3;
  $('btn-back').disabled = state.selection.length === 0;
  const preview = $('points-preview');
  if (state.selection.length >= 2) {
    preview.style.display = 'block';
    preview.textContent = `+${pointsForSelection()} pts`;
  } else preview.style.display = 'none';

  const nextMain = $('next-main');
  nextMain.innerHTML = '';
  if (state.nextPieces[0]) nextMain.appendChild(piecePreview(state.nextPieces[0], 22, true));
  const nextQ = $('next-queue');
  nextQ.innerHTML = '';
  for (let i=1; i<4 && i<state.nextPieces.length; i++) {
    const mini = document.createElement('div');
    mini.className = 'lt-next-mini';
    mini.appendChild(piecePreview(state.nextPieces[i], 12, false));
    nextQ.appendChild(mini);
  }

  $('level-num').textContent = `NIVEL ${state.level}`;
  const goal = goalForLevel(state.level);
  const lp = state.levelProgress;
  const lines = [`${lp.points}/${goal.points} pts`];
  if (goal.longWords) lines.push(`${lp.longWords}/${goal.longWords} pal. ${goal.longLen}+`);
  if (goal.requireFusionWord) lines.push(`${lp.fusionWord ? '✓' : '○'} usa L2`);
  if (goal.requireCascadeCombo) lines.push(`${lp.cascadeCombo ? '✓' : '○'} cascada`);
  $('level-goal').innerHTML = lines.map(l => `<div>${l}</div>`).join('');
  const pct = Math.min(1, lp.points/goal.points);
  const progEl = $('level-progress-text');
  if (progEl) progEl.textContent = `${lp.points}/${goal.points}`;
  const bar = $('level-bar'); bar.innerHTML = '';
  for (let i=0; i<8; i++) {
    const seg = document.createElement('span');
    seg.className = 'lt-level__seg' + (i/8 < pct ? ' on' : '');
    bar.appendChild(seg);
  }

  // Letris HUD row
  const letrisRow = $('letris-row');
  if (letrisRow) {
    letrisRow.innerHTML = '';
    const slots = LETRIS_MAX_SLOTS + (state.config?.letrisExtraSlot || 0);
    for (let i = 0; i < slots; i++) {
      const card = state.letris[i];
      const slot = document.createElement('div');
      slot.className = 'lt-letris-slot';
      if (card) {
        const rarity = LETRIS_RARITY[card.rarity];
        slot.classList.add('lt-letris-card', 'lt-letris-card--sm');
        slot.dataset.id = card.id;
        slot.dataset.rarity = card.rarity;
        slot.style.setProperty('--card-color', card.color);
        slot.style.setProperty('--rarity-bar', rarity.glow);
        slot.title = `[${rarity.label}] ${card.name} — ${card.desc}`;
        slot.innerHTML = `<div class="lt-letris-card__rarity">${rarity.label}</div><div class="lt-letris-card__icon">${card.icon}</div><div class="lt-letris-card__name">${card.name}</div>`;
      } else {
        slot.classList.add('lt-letris-slot--empty');
        slot.textContent = '+';
      }
      letrisRow.appendChild(slot);
    }
  }

  // Intensity pips (10 segmentos, color escalando ámbar→rojo)
  const pipsEl = $('intensity-pips');
  if (pipsEl) {
    pipsEl.innerHTML = '';
    const intensity = getIntensity();
    for (let i = 0; i < 10; i++) {
      const pip = document.createElement('span');
      pip.className = 'lt-intensity-pip';
      if (i < intensity) {
        pip.classList.add('on');
        // color escalando con índice (no con intensity para evitar parpadeo)
        if (i >= 7) pip.classList.add('hot');
        else if (i >= 4) pip.classList.add('warm');
      }
      pipsEl.appendChild(pip);
    }
  }

  const timeEl = $('time-left');
  if (state.timeLeft != null) { timeEl.style.display='block'; timeEl.textContent = fmtTime(state.timeLeft); }
  else timeEl.style.display = 'none';

  const phaseName = $('phase-name');
  if (phaseName) {
    phaseName.textContent = state.phase ? state.phase.name : '—';
    phaseName.style.color = state.phase?.color || '';
  }
  const phaseRule = $('phase-rule');
  if (phaseRule) phaseRule.textContent = state.phase?.rule || '';
  const bossPanel = $('boss-panel');
  if (bossPanel) {
    if (state.boss && state.boss.hp > 0) {
      bossPanel.style.display = 'block';
      $('boss-icon').textContent = state.boss.icon;
      $('boss-icon').style.color = state.boss.color;
      $('boss-name').textContent = state.boss.name;
      $('boss-hp').textContent = `${state.boss.hp}/${state.boss.maxHp}`;
      const pctBoss = Math.max(0, Math.min(1, state.boss.hp / state.boss.maxHp));
      const fill = $('boss-bar-fill');
      fill.style.width = Math.round(pctBoss * 100) + '%';
      fill.style.background = state.boss.color;
      bossPanel.classList.toggle('lt-boss-panel--hit', !!(state._bossHit && performance.now() - state._bossHit.ts < 240));
      bossPanel.classList.toggle('lt-boss-panel--attack', !!(state._bossAttack && performance.now() - state._bossAttack.ts < 500));
    } else {
      bossPanel.style.display = 'none';
    }
  }
  const missionPanel = $('mission-panel');
  if (missionPanel) {
    const m = state.mission;
    missionPanel.style.display = m ? 'block' : 'none';
    if (m) {
      $('mission-label').textContent = m.label;
      $('mission-progress').textContent = missionProgressLabel(m);
    }
  }

  const hist = $('history'); hist.innerHTML = '';
  state.history.forEach(h => {
    const s = LEVEL_STYLES[h.level || 0];
    const li = document.createElement('li');
    li.className = 'lt-history__row';
    li.innerHTML = `<span class="lt-history__dot" style="background:${s.bg}"></span><span class="lt-history__word">${h.word}</span><span class="lt-history__pts">+${h.points}</span>`;
    hist.appendChild(li);
  });
}

function piecePreview(piece, cellSize, glow) {
  const wrap = document.createElement('div');
  wrap.className = (glow ? 'lt-piece-shape--glow ' : '') + 'lt-piece-preview';
  const maxDr = Math.max(...piece.cells.map(c=>c.dr));
  const maxDc = Math.max(...piece.cells.map(c=>c.dc));
  wrap.style.position = 'relative';
  wrap.style.width = ((maxDc+1)*cellSize + maxDc*2) + 'px';
  wrap.style.height = ((maxDr+1)*cellSize + maxDr*2) + 'px';
  for (const cell of piece.cells) {
    const t = document.createElement('div');
    t.className = 'lt-tile';
    t.style.position = 'absolute';
    t.style.left = (cell.dc*(cellSize+2)) + 'px';
    t.style.top  = (cell.dr*(cellSize+2)) + 'px';
    t.style.width = cellSize + 'px';
    t.style.height = cellSize + 'px';
    t.style.background = LEVEL_STYLES[cell.level].bg;
    t.style.color = LEVEL_STYLES[cell.level].fg;
    t.style.fontSize = Math.round(cellSize * 0.5) + 'px';
    t.style.cursor = 'default';
    t.style.display = 'flex';
    t.style.alignItems = 'center';
    t.style.justifyContent = 'center';
    t.textContent = cell.letter;
    wrap.appendChild(t);
  }
  return wrap;
}

function renderBoardDims() {
  const board = $('board');
  const w = COLS * (CELL + GAP) + GAP + PAD * 2;
  const h = ROWS * (CELL + GAP) + GAP + PAD * 2;
  board.style.width = w + 'px';
  board.style.height = h + 'px';
  board.style.setProperty('--phase-color', state.phase?.color || '#06D6A0');
  board.dataset.phase = state.phase?.id || 'none';
  const grid = $('grid');
  grid.style.width = w + 'px';
  grid.style.height = h + 'px';
  grid.style.backgroundSize = `${CELL+GAP}px ${CELL+GAP}px`;
  grid.style.backgroundPosition = `${PAD}px ${PAD}px`;
}

function renderFlashAndOverlay() {
  const fEl = $('flash');
  fEl.innerHTML = '';
  fEl.className = 'lt-flash';
  fEl.style.display = state.flash ? 'flex' : 'none';
  if (state.flash) {
    fEl.classList.add('lt-flash--' + state.flash.kind);
    if (state.flash.kind === 'valid') {
      fEl.innerHTML = `<div class="lt-flash__word">${state.flash.word}</div><div class="lt-flash__pts">+${state.flash.points} pts</div>`;
    } else if (state.flash.kind === 'levelup') {
      fEl.innerHTML = `<div class="lt-flash__word">${state.flash.word}</div><div class="lt-flash__pts">${state.flash.subtitle}</div>`;
    } else {
      fEl.innerHTML = `<div class="lt-flash__word">¿${state.flash.word}? NO ES VÁLIDA</div>`;
    }
  }
  // línea de peligro + countdown badge
  const lineEl = $('overflow-line');
  lineEl.style.top = (PAD + state.dangerLine * (CELL + GAP)) + 'px';
  if (state._dangerLineDescended) {
    lineEl.classList.remove('lt-overflow-line--warn');
    void lineEl.offsetWidth;
    lineEl.classList.add('lt-overflow-line--warn');
    setTimeout(() => lineEl.classList.remove('lt-overflow-line--warn'), 1500);
    state._dangerLineDescended = false;
  }
  // badge countdown
  if (state.config?.allowGameOver !== false) {
    const remaining = Math.max(0, Math.ceil(state.nextDropTime - state.levelTime));
    let badge = $('danger-countdown');
    if (!badge) {
      badge = document.createElement('div');
      badge.id = 'danger-countdown';
      badge.className = 'lt-danger-countdown';
      $('board').appendChild(badge);
    }
    badge.textContent = remaining + 's';
    badge.style.top = (PAD + state.dangerLine * (CELL + GAP) - 16) + 'px';
    badge.classList.toggle('lt-danger-countdown--urgent', remaining <= 8);
    // flash si grace recién aplicado
    if (state._lastGrace && performance.now() - state._lastGrace.ts < 100) {
      const pop = document.createElement('div');
      pop.className = 'lt-grace-pop';
      pop.textContent = '+' + state._lastGrace.amount + 's';
      pop.style.top = (PAD + state.dangerLine * (CELL + GAP) - 14) + 'px';
      $('board').appendChild(pop);
      setTimeout(() => pop.remove(), 1100);
      state._lastGrace = null;
    }
  }
  $('pause-overlay').style.display = (state.paused && !state.gameOver) ? 'flex' : 'none';
}

function render() {
  if (!state.config) return;
  renderBoardDims();
  renderTopAndPanels();
  renderTiles();
  renderFlashAndOverlay();
}

// ====================== TECLADO ======================
function bindKeyboard() {
  document.addEventListener('keydown', (e) => {
    if (!state.config || state.gameOver) return;
    if (e.key === 'p' || e.key === 'P') { state.paused = !state.paused; render(); return; }
    if (e.key === 'm' || e.key === 'M') {
      audioSetMuted(!audioIsMuted());
      const b = document.getElementById('btn-mute'); if (b) b.textContent = audioIsMuted() ? '🔇' : '🔊';
      return;
    }
    if (e.key === 'n' || e.key === 'N') {
      audioSetMusicMuted(!audioIsMusicMuted());
      const b = document.getElementById('btn-music'); if (b) b.textContent = audioIsMusicMuted() ? '♪̸' : '♪';
      return;
    }
    if (state.paused) return;
    const k = e.key;
    // arrows + WASD
    if (k === 'ArrowLeft'  || k === 'a' || k === 'A') { if (tryMove(0,-1)) render(); e.preventDefault(); return; }
    if (k === 'ArrowRight' || k === 'd' || k === 'D') { if (tryMove(0, 1)) render(); e.preventDefault(); return; }
    if (k === 'ArrowDown'  || k === 's' || k === 'S') { if (tryMove(1, 0)) { state.dropTimer = 0; render(); } e.preventDefault(); return; }
    if (k === 'ArrowUp'    || k === 'w' || k === 'W') { if (tryRotate()) render(); e.preventDefault(); return; }
    if (k === ' ')      { hardDrop(); render(); e.preventDefault(); return; }
    if (k === 'Enter')  { submitWord(); e.preventDefault(); return; }
    if (k === 'Backspace') { backspace(); e.preventDefault(); return; }
    if (k === 'Escape') { clearSelection(); e.preventDefault(); return; }
  });
}

// ====================== API PÚBLICA ======================
const LetrisGame = {
  start(config) {
    Object.assign(state, makeInitialState());
    state.config = config;
    state.dictionary = config.dictionary;
    state.rng = config.seed != null ? mulberry32(config.seed) : Math.random;
    state.timeLeft = config.timeLimit ?? null;
    state.listeners = Object.assign({ onScore:null, onLevelUp:null, onGameOver:null, onWord:null, onCoins:null, onLetrisChange:null, onTargetFound:null, onAllTargets:null }, config.listeners || {});
    state._foundTargets = new Set();
    state._justLanded = new Set();
    state._justMerged = new Set();
    state._justPushed = new Set();
    state._justSlammed = new Set();
    state._slamLanding = false;
    state._shockwaves = [];
    state._absorbs = [];
    state._comboBanner = 0;
    state._letterQueue = [];
    state._recentLetters = [];
    state._lastGrace = null;
    state._mysteryTimer = 0;
    state._mysteryNext = null;
    state._idleSec = 0;
    state._hintTileId = null;
    state.phase = null;
    state.boss = null;
    state.mission = null;
    state._directorRelief = 0;
    if (config.startGrace) state.nextDropTime += config.startGrace;
    _scoreDisplay = 0;
    startMusic();
    document.querySelectorAll('.lt-tile-abs, .lt-shockwave, .lt-particle, .lt-score-pop, .lt-absorb-vfx, .lt-combo-banner, .lt-slot-overlay, .lt-word-combo, .lt-selpath, .lt-grace-pop, .lt-letris-modal, .lt-letris-fx, .lt-bomb-blast, .lt-bomb-spark, .lt-levelup-ring, .lt-levelup-spark, .lt-board-chroma, #danger-countdown').forEach(el => el.remove());
    tileEls.clear();
    ghostEls = [];
    _lastPieceSig = null;
    pieceEls = [];
    refillNext();
    setupPhaseAndMission();
    spawnPiece();
    render();
  },
  pause()  { state.paused = true; render(); },
  resume() { state.paused = false; render(); },
  abandon() {
    state.config = null; state.gameOver = true;
    document.querySelectorAll('.lt-tile-abs, .lt-shockwave, .lt-particle, .lt-score-pop').forEach(el => el.remove());
    tileEls.clear();
    pieceEls = [];
    stopMusic();
  },
  isOver:    () => state.gameOver,
  getScore:  () => state.score,
  getLevel:  () => state.level,
  getFoundTargets: () => state._foundTargets ? [...state._foundTargets] : [],
  // Touch UI controls
  rotate()    { if (!state.config || state.gameOver || state.paused) return; if (tryRotate()) render(); },
  moveLeft()  { if (!state.config || state.gameOver || state.paused) return; if (tryMove(0,-1)) render(); },
  moveRight() { if (!state.config || state.gameOver || state.paused) return; if (tryMove(0, 1)) render(); },
  softDrop()  { if (!state.config || state.gameOver || state.paused) return; if (tryMove(1, 0)) { state.dropTimer = 0; render(); } },
  hardDrop()  { if (!state.config || state.gameOver || state.paused) return; hardDrop(); render(); },
  getDuelSnapshot() {
    let g = '';
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const id = state.board[r][c];
        const t = id ? state.tiles.get(id) : null;
        g += t ? (t.special === 'blocker' ? '#' : t.letter) : '.';
      }
    }
    return { score: state.score, level: state.level, dangerLine: state.dangerLine, grid: g };
  },
  applyGarbageRows(n) {
    if (state.gameOver || !state.config) return;
    if (n <= 0) return;
    // shift all tiles up by n rows
    for (const tile of state.tiles.values()) {
      for (const cell of tile.cells) cell.r -= n;
    }
    // overflow → game over
    for (const tile of state.tiles.values()) {
      if (tile.cells.some(c => c.r < 0)) {
        state.gameOver = true;
        state.listeners.onGameOver?.(state.score);
        return;
      }
    }
    // rebuild board map
    state.board = makeEmptyBoard();
    for (const tile of state.tiles.values()) {
      for (const {r, c} of tile.cells) state.board[r][c] = tile.id;
    }
    // fill bottom n rows with random letters
    for (let r = ROWS - n; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (state.board[r][c]) continue;
        const tile = makeTile(randomLetter(), 0, [{r, c}]);
        placeTile(tile);
        state._justSlammed?.add(tile.id);
      }
    }
    render();
  },
  applyBlockerTiles(n) {
    if (state.gameOver || !state.config) return;
    if (n <= 0) return;
    const now = performance.now();
    let placed = 0;
    // pick random empty cells in the upper-mid area (rows 4..14)
    for (let attempt = 0; attempt < n * 12 && placed < n; attempt++) {
      const r = 4 + Math.floor(Math.random() * 11);
      const c = Math.floor(Math.random() * COLS);
      if (state.board[r][c]) continue;
      const id = nextTileId++;
      const tile = {
        id, letter: '▓', level: 0,
        special: 'blocker',
        blockerUntil: now + 12000,
        cells: [{ r, c }],
      };
      state.tiles.set(id, tile);
      state.board[r][c] = id;
      state._justSlammed?.add(id);
      placed++;
    }
    render();
  },
  bindUI(handlers) {
    document.getElementById('btn-pause').addEventListener('click', () => { state.paused = !state.paused; render(); });
    document.getElementById('btn-restart').addEventListener('click', handlers.onRestart);
    document.getElementById('btn-quit')?.addEventListener('click', handlers.onQuit);
    document.getElementById('btn-send').addEventListener('click', submitWord);
    document.getElementById('btn-back').addEventListener('click', backspace);
    document.getElementById('btn-clear').addEventListener('click', clearSelection);
    document.getElementById('btn-pause-resume').addEventListener('click', () => { state.paused = false; render(); });
    // Audio toggles
    const btnMute = document.getElementById('btn-mute');
    const btnMusic = document.getElementById('btn-music');
    if (btnMute) {
      const refresh = () => btnMute.textContent = audioIsMuted() ? '🔇' : '🔊';
      refresh();
      btnMute.addEventListener('click', () => { audioSetMuted(!audioIsMuted()); refresh(); });
    }
    if (btnMusic) {
      const refresh = () => btnMusic.textContent = audioIsMusicMuted() ? '♪̸' : '♪';
      refresh();
      btnMusic.addEventListener('click', () => { audioSetMusicMuted(!audioIsMusicMuted()); refresh(); });
    }
    setupGridPointer();
  },
  init() {
    bindKeyboard();
    state.lastTick = performance.now();
    requestAnimationFrame(tick);
  },
};

export { LetrisGame, LEVEL_STYLES, LEVEL_GOALS };
